// comms.js
import axios from 'axios';
import io from 'socket.io-client';
import { offlineStorage } from '../services/offlineStorage';
import { connectionManager } from '../services/connectionManager';
import { resolveVehicleUnitId } from '../utils/resolveVehicleUnitId';

let socketConnection=null;
let outGoingMessageNumber = 1;

// Connection status management
let connectionStatus = 'disconnected';
let connectionStatusCallbacks = [];

// Active route tracking for automatic room rejoin on reconnection
let currentRouteId = null;

/** Optional handler: receives dispatch MSG text while Flag Trip awaits server feedback */
let flagTripAwaitingMessageHandler = null;

/** Optional handler: receives parsed TRIPERR payloads while a flag-trip modal is open. */
let flagTripErrorHandler = null;

/**
 * After `completeWithPricing`, wait for inbound `PRICE|Ok|…` before sending `COMPM`.
 * If no ack arrives, fall back to COMPM after {@link PRICE_ACK_TIMEOUT_MS}.
 */
let pendingPriceComplete = null;
const PRICE_ACK_TIMEOUT_MS = 12_000;

function clearPendingPriceComplete() {
  if (pendingPriceComplete?.timeoutId) {
    clearTimeout(pendingPriceComplete.timeoutId);
  }
  pendingPriceComplete = null;
}

/**
 * Outstanding PRICE sent by comms.finalizePrice() — a fare finalization that must be
 * acknowledged BEFORE payment collection and must never fall through to COMPM
 * (DRIVERAPP_HOSTED_PAGE_PAYMENTS.md "PRICE|Ok → load payment state → choose method").
 * At most one PRICE operation is outstanding per socket; the ack does not reliably
 * name a trip, so it is associated with whichever operation is pending.
 */
let pendingPriceFinalize = null;

function settlePriceFinalize(outcome, payload) {
  if (!pendingPriceFinalize) return false;
  const pending = pendingPriceFinalize;
  pendingPriceFinalize = null;
  clearTimeout(pending.timeoutId);
  if (pending.socket && pending.listener) {
    try { pending.socket.off('Command', pending.listener); } catch (e) { /* socket gone */ }
  }
  if (outcome === 'resolve') pending.resolve(payload);
  else pending.reject(payload instanceof Error ? payload : new Error(String(payload || 'PRICE failed')));
  return true;
}

/**
 * Parse a raw Command frame ("<msgId>|PRICE|Ok[|<trip>]" or "<msgId>|PRICE|<trip>|Ok")
 * for the pending finalize. Live Dispatch's setupSocketHandlers parser does the same
 * job, so in Live mode whichever runs first settles it and the other is a no-op;
 * Itinerary mode has no other Command parser and relies on this one.
 */
function priceAckListenerFor(trip) {
  return (raw) => {
    if (!pendingPriceFinalize || String(pendingPriceFinalize.tripNumber) !== trip) return;
    const parts = String(raw || '').split('|');
    const idx = parts.indexOf('PRICE');
    if (idx === -1) return;
    const t1 = String(parts[idx + 1] || '').trim();
    const t2 = String(parts[idx + 2] || '').trim();
    const isOk = (s) => s.toLowerCase() === 'ok';
    if (isOk(t1) || isOk(t2)) {
      tryResolvePriceFinalize(isOk(t1) ? t2 : t1);
    } else if (t1) {
      rejectPriceFinalize(new Error(`Dispatch rejected the fare: ${t2 || t1}`));
    }
  };
}

/** @returns {boolean} true if a pending finalization matched this ack and was resolved */
function tryResolvePriceFinalize(ackTripToken) {
  if (!pendingPriceFinalize) return false;
  const want = String(pendingPriceFinalize.tripNumber).trim();
  const got = String(ackTripToken || '').trim();
  if (got && want && got !== want) return false;
  return settlePriceFinalize('resolve', { tripNumber: want });
}

function rejectPriceFinalize(error) {
  return settlePriceFinalize('reject', error);
}

/** @returns {boolean} true if a pending completion was resolved and COMPM was sent */
function tryCompleteAfterPriceAck(ackTripToken) {
  if (!pendingPriceComplete) return false;
  const want = String(pendingPriceComplete.tripNumber).trim();
  const got = String(ackTripToken ?? '').trim();
  if (got && got !== want) return false;
  clearTimeout(pendingPriceComplete.timeoutId);
  const trip = pendingPriceComplete.tripNumber;
  clearPendingPriceComplete();
  comms.sendSocket(`COMPM|${trip}`, { skipOfflineQueue: true });
  return true;
}

// Route data persistence keys
const ROUTE_DATA_KEY = 'persistedRouteData';
const ROUTE_PERSIST_TIMESTAMP_KEY = 'routeDataPersistedAt';
const MAX_ROUTE_DATA_AGE_MS = 4 * 60 * 60 * 1000; // 4 hours

// Notify all subscribers of connection status change
const notifyConnectionStatus = (status) => {
    const previousStatus = connectionStatus;
    connectionStatus = status;
    connectionStatusCallbacks.forEach(cb => {
        try {
            cb(status, previousStatus);
        } catch (err) {
            console.error('Error in connection status callback:', err);
        }
    });
};

/** Transport-level failures when the host is down, wrong URL, or blocked — not app logic bugs */
const isBenignSocketTransportError = (error) => {
    if (!error) return false;
    const msg = String(error.message || error).toLowerCase();
    return (
        msg.includes('xhr poll error') ||
        msg.includes('xhr post error') ||
        msg.includes('websocket error') ||
        msg.includes('websocket is closed before the connection is established') ||
        msg.includes('failed to fetch') ||
        msg.includes('network error') ||
        msg.includes('load failed') ||
        msg.includes('connection refused')
    );
};

const comms = {
    authenticate: (driverNumber, pin, vehicleId = null) => {
        const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
        const req = {
            driverNumber,
            pin: pin.toString().padStart(4, "0"),
            unitId: fleetSettings.unitId,
            vehicleId: vehicleId
        };

        return axios.post(`${fleetSettings.fleetUrl}/API/Driver/Auth`, req)

            .then((res) => {
                if(res.error){
                    console.error('Authentication failed:', res.error);

                    throw res.error;
                }else{
                    localStorage.setItem('userSettings', JSON.stringify(res.data));
                    return res.data;
                }
            })
            .catch((err) => {
                console.error('Authentication failed:', err);

                throw err;
            });
    },
    
    // New simplified authorization method with offline support
    authorize: (driverNumber, pin, faceNumber = null, vehicleId = null, offlineMode = false) => {
        const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
        
        // If explicitly in offline mode, use cached credentials
        if (offlineMode) {
            const cachedUserSettings = localStorage.getItem('userSettings');
            const cachedAuthData = localStorage.getItem('lastAuthData');
            
            if (cachedUserSettings && cachedAuthData) {
                console.log('Using cached authorization (offline mode)');
                return Promise.resolve(JSON.parse(cachedAuthData));
            } else {
                return Promise.reject(new Error('No cached authorization data available'));
            }
        }
        
        const req = {
            driverNumber,
            faceNumber,
            pin: pin.toString().padStart(4, "0")
        };

        // Include vehicleId if provided (required when !fleetSettings.driverDevice)
        if (vehicleId) {
            req.vehicleId = vehicleId;
        }

        return axios.post(`${fleetSettings.fleetUrl}/API/Driver/Authorize`, req, {
            timeout: 10000, // 10 second timeout
            // Scoped to this call only (NOT axios.defaults — that broke unrelated
            // cross-origin calls like getSettings()'s config-server fetch, which
            // doesn't support/need credentialed CORS). Needed so the browser stores
            // the express-session cookie this endpoint sets, for splitRoute() to send back.
            withCredentials: true,
        })
            .then((res) => {
                if (res.data.error) {
                    console.error('Authorization failed:', res.data.error);
                    throw new Error(res.data.error);
                } else {
                    // Store only essential user settings that survive reload
                    const userSettings = {
                        Id: res.data.driverId,
                        Code: driverNumber || faceNumber,
                        PIN: pin,
                        ...res.data.driverObject,
                        // Ensure UnitID is available for joinDriverAppRoom (server expects PascalCase UnitID)
                        // driverObject has camelCase unitId, dispatchData.vehicleObject has unitID.
                        //
                        // The VEHICLE ACTUALLY ASSIGNED to this shift is checked FIRST. The driver
                        // record's own unitId is only a fallback: it is a driver-level attribute that
                        // can hold a stale/placeholder value (e.g. a test driver carrying "1234561"
                        // while dispatch has them on unit 999). Registering the socket under that
                        // bogus unit means the server cannot resolve the socket to any vehicle, and
                        // every vehicle-scoped call fails with "Driver socket has no authoritative
                        // vehicle identity" — which broke chat/Messages entirely.
                        UnitID: res.data.dispatchData?.vehicleObject?.unitID
                            || res.data.dispatchData?.vehicleObject?.UnitID
                            || res.data.dispatchData?.vehicleObject?.unitId
                            || res.data.driverObject?.unitId
                            || res.data.driverObject?.UnitID,
                        // Ensure VehicleID is available for inspection calls
                        // This is the VEHICLE's Id, not the driver's Id
                        VehicleID: res.data.dispatchData?.vehicleObject?.id || res.data.dispatchData?.vehicleObject?.Id || res.data.dispatchData?.vehicleId,
                        dispatchData: res.data.dispatchData || null,
                    };
                    localStorage.setItem('userSettings', JSON.stringify(userSettings));
                    
                    // Store complete auth data for offline recovery
                    localStorage.setItem('lastAuthData', JSON.stringify(res.data));
                    localStorage.setItem('lastAuthTimestamp', Date.now().toString());

                    // Bind this socket to the driver's vehicle NOW that identity exists.
                    //
                    // joinDriverAppRoom() is otherwise only attempted on socket
                    // connect/reconnect — but the socket comes up BEFORE auth completes,
                    // when userSettings isn't in localStorage yet, so its
                    // `userSettings && unitID` guard silently skipped the emit and never
                    // retried. The server sets rooms[UnitID].driverAppID only from that
                    // emit, and vehicleForSocket() resolves a socket's vehicle solely from
                    // it — so an unbound socket gets rejected with "Driver socket has no
                    // authoritative vehicle identity". That is why chat failed for every
                    // driver except one who happened to open the Messages modal, which was
                    // the only other caller of joinDriverApp.
                    try {
                        comms.joinDriverAppRoom();
                    } catch (bindErr) {
                        console.warn('joinDriverAppRoom after authorize failed:', bindErr);
                    }

                    return res.data;
                }
            })
            .catch((err) => {
                console.error('Authorization error:', err);
                
                // Check if this is a 401 with explicit invalid credentials
                if (err.response?.status === 401 && 
                    err.response?.data?.error === 'Invalid credentials') {
                    // This is an explicit auth failure - don't use cache
                    throw new Error('Invalid credentials');
                }
                
                // For network errors or timeouts, try to use cached data
                const cachedUserSettings = localStorage.getItem('userSettings');
                const cachedAuthData = localStorage.getItem('lastAuthData');
                
                if (cachedUserSettings && cachedAuthData) {
                    const authData = JSON.parse(cachedAuthData);
                    const lastAuthTime = parseInt(localStorage.getItem('lastAuthTimestamp') || '0');
                    const hoursSinceAuth = (Date.now() - lastAuthTime) / (1000 * 60 * 60);
                    
                    // Use cached data if less than 24 hours old
                    if (hoursSinceAuth < 24) {
                        console.warn('Network error during auth - using cached data (last auth: ' + 
                            hoursSinceAuth.toFixed(1) + ' hours ago)');
                        return authData;
                    }
                }
                
                // No valid cache or cache too old
                throw err;
            });
    },
    
    // DEV ONLY: point the app at a specific AutoDispatch backend without changing the
    // config server. When REACT_APP_BACKEND_URL is set (see .env.local), fleetUrl is
    // forced so every /API call and the Socket.IO connection go there. Inert in
    // production (env var unset) — branding/flags still come from the cloud config,
    // only the backend URL is swapped. Mirrors RiderApp's VITE_BACKEND_URL.
    applyBackendOverride: (settings) => {
        const override = process.env.REACT_APP_BACKEND_URL;
        if (!override || !settings || typeof settings !== 'object') return settings;
        if (settings.fleetUrl !== override) {
            console.warn('[dev] fleetUrl overridden ->', override, '(was', settings.fleetUrl + ')');
        }
        return { ...settings, fleetUrl: override };
    },

    // DEV ONLY: point the RiderApp payment QR at a nonstandard host. The QR host is
    // normally derived from the fleet code (https://<fleet>.riderapp.net/pay), which is
    // correct in production but wrong on staging, where the backend override sends the
    // trip to a staging AutoDispatch while the QR would still resolve against the
    // production RiderApp — a scan would look up a trip the production server has never
    // seen. Setting REACT_APP_RIDERAPP_URL keeps the QR on the same environment as the
    // backend. Inert in production (env var unset), and it never overrides a riderAppUrl
    // the config server already supplies for an approved nonstandard host.
    applyRiderAppOverride: (settings) => {
        const override = process.env.REACT_APP_RIDERAPP_URL;
        if (!override || !settings || typeof settings !== 'object') return settings;
        if (settings.riderAppUrl) return settings;
        console.warn('[dev] riderAppUrl overridden ->', override);
        return { ...settings, riderAppUrl: override };
    },

    getSettings: (fleetCode, useCache = false) => {
        const req = { unitId: fleetCode };
        
        // If explicitly using cache (offline mode), return cached settings
        if (useCache) {
            const cachedSettings = localStorage.getItem('fleetSettings');
            if (cachedSettings) {
                console.log('Using cached fleet settings (offline mode)');
                return Promise.resolve(comms.applyRiderAppOverride(comms.applyBackendOverride(JSON.parse(cachedSettings))));
            }
        }
        
        // Get config server URL from window.APP_CONFIG (set in index.html)
        const configServerUrl = window.APP_CONFIG?.configServerUrl || 'https://admin.tranware.net/api/config/driverapp-config';
        
        return axios.get(configServerUrl + '/' + fleetCode.toUpperCase(), req)
            .then((res) => {
                // Store as last known good config
                const settings = comms.applyRiderAppOverride(comms.applyBackendOverride(res.data));
                localStorage.setItem('fleetSettings', JSON.stringify(settings));
                localStorage.setItem('fleetSettingsTimestamp', Date.now().toString());
                console.log('Fleet settings refreshed from server');
                return settings;
            })
            .catch((err) => {
                console.error('Settings retrieval failed:', err);
                
                // Fall back to last known good config
                const cachedSettings = localStorage.getItem('fleetSettings');
                if (cachedSettings) {
                    console.warn('Using last known good fleet settings due to fetch error');
                    return comms.applyRiderAppOverride(comms.applyBackendOverride(JSON.parse(cachedSettings)));
                }
                
                throw err;
            });
    },
    getFixedRoute: (fixedRouteId) => {
      const fleetSettings=JSON.parse(localStorage.getItem('fleetSettings'))
        return axios.get(`${fleetSettings.fleetUrl}/API/getFixedRoute`, { params: { id: fixedRouteId } })
            .then((res) => {
                console.log('Fixed route data fetched:', res.data);
                return res.data;
            })
            .catch((err) => {
                console.error('Fixed route data fetch failed:', err);
                throw err;
            });
    },
    
    // New enhanced route details endpoint that returns expanded routeDetails object
    getRouteDetails: (routeId, scheduleId = null, runId = null, vehicleId = null) => {
        const params = { id: routeId };
        if (scheduleId) params.scheduleId = scheduleId;
        if (runId) params.runId = runId;
        if (vehicleId) params.vehicleId = vehicleId;
        const fleetSettings=JSON.parse(localStorage.getItem('fleetSettings'))
        
        return axios.get(`${fleetSettings.fleetUrl}/API/Driver/RouteDetails`, { params })
            .then((res) => {
                console.log('Enhanced route details fetched:', res.data);
                // Return the data portion of the response (which contains the complete route data)
                return res.data.success ? res.data.data : res.data;
            })
            .catch((err) => {
                console.error('Enhanced route details fetch failed:', err);
                throw err;
            });
    },
    getTripAssets: function (vin) {
        if (!vin || vin === 'undefined') {
            vin = '';
        }
        const user = JSON.parse(localStorage.getItem('userSettings'));
        const fleetSettings=JSON.parse(localStorage.getItem('fleetSettings'))
        return fetch(fleetSettings.fleetUrl + "/API/Driver/TripAsset?paxId=" + user.Id + "&vin=" + vin || '')
            .then((response) => response.json())
            .then((data) => {
                console.log(data)
                return (data)
            })
            .catch((error) => {
                console.error('Error fetching trips:', error);
                throw error;
            });

    },
    connectSocket: (url) => {
        return new Promise((resolve, reject) => {
          const fleetSettings=JSON.parse(localStorage.getItem('fleetSettings'))
            try {
                let settled = false;
                const settle = (fn) => {
                    if (settled) return;
                    settled = true;
                    fn();
                };

                // Check if browser is offline - don't even try to connect
                const isOffline = !navigator.onLine;
                
                // Socket.IO with reconnection options
                socketConnection = io(url || fleetSettings.fleetUrl, {
                    reconnection: true,
                    reconnectionAttempts: Infinity, // Keep trying indefinitely for offline-first
                    reconnectionDelay: isOffline ? 5000 : 1000, // Slower retries when offline
                    reconnectionDelayMax: 30000,
                    timeout: 10000,
                    autoConnect: !isOffline // Don't auto-connect if offline
                });
                
                socketConnection.io.on('error', (error) => {
                    if (isBenignSocketTransportError(error)) {
                        return;
                    }
                    if (!navigator.onLine) {
                        return;
                    }
                    console.error('Socket.io transport error:', error);
                });
                
                // Initialize connection manager with socket
                connectionManager.setSocket(socketConnection);
                
                socketConnection.on('connect', () => {
                    console.log('Socket connected successfully');
                    notifyConnectionStatus('connected');
                    
                    // Rejoin rooms after connection/reconnection
                    comms.joinDriverAppRoom();
                    
                    settle(() => resolve(socketConnection));
                });
                
                socketConnection.on('disconnect', (reason) => {
                    console.warn('Socket disconnected:', reason);
                    notifyConnectionStatus('disconnected');
                    // A fare finalization cannot be trusted across a drop — the caller
                    // reloads state and resends PRICE rather than completing optimistically.
                    rejectPriceFinalize(new Error('Connection lost before the fare was confirmed. Please retry.'));
                });
                
                socketConnection.on('reconnecting', (attemptNumber) => {
                    console.log('Socket reconnecting, attempt:', attemptNumber);
                    notifyConnectionStatus('reconnecting');
                });
                
                socketConnection.on('reconnect', (attemptNumber) => {
                    console.log('Socket reconnected after', attemptNumber, 'attempts');
                    notifyConnectionStatus('connected');
                    
                    // Rejoin rooms after reconnection
                    comms.joinDriverAppRoom();
                });
                
                socketConnection.on('reconnect_error', (error) => {
                    if (isBenignSocketTransportError(error)) {
                        notifyConnectionStatus('error');
                        return;
                    }
                    console.error('Socket reconnection error:', error);
                    notifyConnectionStatus('error');
                });
                
                socketConnection.on('reconnect_failed', () => {
                    console.error('Socket reconnection failed after max attempts');
                    notifyConnectionStatus('failed');
                });
                
                socketConnection.on('connect_error', (error) => {
                    if (!navigator.onLine) {
                        console.log('Socket connect error while offline - expected');
                        notifyConnectionStatus('offline');
                        return;
                    }
                    if (isBenignSocketTransportError(error)) {
                        console.warn('Socket connect error (retrying):', error.message || error);
                    } else {
                        console.error('Socket connection error:', error);
                    }
                    notifyConnectionStatus('error');
                });
                
                // Handle server-authoritative route updates (server-wins)
                socketConnection.on('routeUpdate', (data) => {
                    console.log('Received authoritative route update from server');
                    connectionManager.handleServerData(data);
                });
                
                // Set a timeout for initial connection
                // When offline, resolve with the socket anyway (will connect when online)
                const timeoutMs = navigator.onLine ? 10000 : 2000; // 2 seconds when offline, 10 when online
                setTimeout(() => {
                    if (settled) return;
                    if (socketConnection.connected) return;
                    console.warn(
                        'Socket not connected after initial wait — continuing; client will keep reconnecting'
                    );
                    notifyConnectionStatus(!navigator.onLine ? 'offline' : 'disconnected');
                    settle(() => resolve(socketConnection));
                }, timeoutMs);
                
            } catch (error) {
                console.error('Error creating socket:', error);
                reject(error);
            }
        });
    },
    
    // Join the driver app room (called on connect and reconnect)
    joinDriverAppRoom: () => {
        const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings'));
        const userSettings = JSON.parse(localStorage.getItem('userSettings'));
        
        // Shared resolver — same value the Messages/chat UI uses, so the socket room and
        // the chat component can never disagree (see utils/resolveVehicleUnitId.js).
        const unitID = resolveVehicleUnitId({ userSettings });
        
        if (socketConnection && fleetSettings && userSettings && unitID) {
            console.log('Joining driver app room for unit:', unitID);
            socketConnection.emit('joinDriverApp', { UnitID: unitID });
        }
        
        // Also rejoin route room if we have an active route
        if (socketConnection && currentRouteId) {
            console.log('Rejoining route room for route:', currentRouteId);
            socketConnection.emit('joinRoute', { routeId: currentRouteId });
        }
    },
    
    // Set active route for automatic room management
    setActiveRoute: (routeId) => {
        currentRouteId = routeId;
        if (socketConnection && routeId) {
            console.log('Setting active route and joining room:', routeId);
            socketConnection.emit('joinRoute', { routeId: routeId });
        }
    },
    
    // Get current active route ID
    getActiveRoute: () => {
        return currentRouteId;
    },
    
    // Persist route data to localStorage for session recovery and offline mode
    persistRouteData: (routeData) => {
        if (!routeData) return;
        
        try {
            // Use offlineStorage service for unified storage
            offlineStorage.saveRouteData(routeData);
            
            // Also maintain legacy format for backward compatibility
            const persistData = {
                routeData: routeData,
                persistedAt: Date.now(),
                activeDate: routeData.activeDate || new Date().toISOString().split('T')[0]
            };
            localStorage.setItem(ROUTE_DATA_KEY, JSON.stringify(persistData));
            localStorage.setItem(ROUTE_PERSIST_TIMESTAMP_KEY, persistData.persistedAt.toString());
            
            // Also update active route ID for room management
            if (routeData.routeId) {
                currentRouteId = routeData.routeId;
            }
            
            console.log('Route data persisted to localStorage');
        } catch (err) {
            console.error('Failed to persist route data:', err);
        }
    },
    
    // Get persisted route data if valid (not stale, same day)
    // First tries offlineStorage, falls back to legacy format
    getPersistedRouteData: () => {
        try {
            // Try offlineStorage first (newer, more robust)
            const offlineData = offlineStorage.getRouteData();
            if (offlineData) {
                // Update active route ID for room management
                if (offlineData.routeId) {
                    currentRouteId = offlineData.routeId;
                }
                console.log('Retrieved route data from offlineStorage');
                return offlineData;
            }
            
            // Fall back to legacy format
            const persistedStr = localStorage.getItem(ROUTE_DATA_KEY);
            if (!persistedStr) return null;
            
            const persisted = JSON.parse(persistedStr);
            const now = Date.now();
            const age = now - persisted.persistedAt;
            
            // Check if data is too old
            if (age > MAX_ROUTE_DATA_AGE_MS) {
                console.log('Persisted route data is stale, clearing...');
                comms.clearPersistedRouteData();
                return null;
            }
            
            // Check if activeDate matches today
            const today = new Date().toISOString().split('T')[0];
            const persistedDate = persisted.activeDate?.split('T')[0];
            
            if (persistedDate && persistedDate !== today) {
                console.log('Persisted route data is from different day, clearing...');
                comms.clearPersistedRouteData();
                return null;
            }
            
            // Update active route ID
            if (persisted.routeData?.routeId) {
                currentRouteId = persisted.routeData.routeId;
            }
            
            console.log('Retrieved valid persisted route data (legacy)');
            return persisted.routeData;
        } catch (err) {
            console.error('Failed to get persisted route data:', err);
            return null;
        }
    },
    
    // Clear persisted route data (both new and legacy)
    clearPersistedRouteData: () => {
        localStorage.removeItem(ROUTE_DATA_KEY);
        localStorage.removeItem(ROUTE_PERSIST_TIMESTAMP_KEY);
        offlineStorage.clearRouteData();
        currentRouteId = null;
        console.log('Persisted route data cleared');
    },
    
    // Subscribe to connection status changes
    // Returns unsubscribe function
    onConnectionStatusChange: (callback) => {
        connectionStatusCallbacks.push(callback);
        // Immediately call with current status
        callback(connectionStatus, null);
        return () => {
            connectionStatusCallbacks = connectionStatusCallbacks.filter(cb => cb !== callback);
        };
    },
    
    // Get current connection status
    getConnectionStatus: () => {
        return connectionStatus;
    },
    
    // Manually trigger reconnection attempt
    triggerReconnect: () => {
        if (socketConnection) {
            console.log('Manually triggering socket reconnection...');
            notifyConnectionStatus('reconnecting');
            socketConnection.connect();
        } else {
            console.warn('No socket connection to reconnect');
            // Try to create a new connection
            const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings'));
            if (fleetSettings && fleetSettings.fleetUrl) {
                void comms.connectSocket(fleetSettings.fleetUrl).catch(() => {});
            }
        }
    },
    
    postTowing: function (data) {
     
        const fleetSettings=JSON.parse(localStorage.getItem('fleetSettings'))
        const req = data;
        console.log("comms.postTowing req:", req);

        return axios.post(`${fleetSettings.fleetUrl}/API/Driver/Towing`, req, {
            headers: {
                'Content-Type': 'application/json',
            }
        })
            .then((res) => {
                console.log("comms.postTowing res:", res.data);
                if (!res.data.error) {
                    return res.data;
                } else {
                    throw new Error(res.data.error);
                }
            })
            .catch((error) => {
                console.error('Error posting towing data:', error.message || error);
                throw error;
            });
    },
    postTowingComplete: function (data) {
        const fleetSettings=JSON.parse(localStorage.getItem('fleetSettings'))

        const req = data;
        console.log("comms.postTowing req:", req);

        return axios.post(`${fleetSettings.fleetUrl}/API/Driver/TowingComplete`, req, {
            headers: {
                'Content-Type': 'application/json',
            }
        })
            .then((res) => {
                console.log("comms.postTowing res:", res.data);
                if (!res.data.error) {
                    return res.data;
                } else {
                    throw new Error(res.data.error);
                }
            })
            .catch((error) => {
                console.error('Error posting towing data:', error.message || error);
                throw error;
            });
    },
    getInspectionItems: async () => {
        try {
          const user = JSON.parse(localStorage.getItem('userSettings')) || {};
        const fleetSettings=JSON.parse(localStorage.getItem('fleetSettings'))
          // VehicleID is the VEHICLE's Id (not the driver's Id)
          // May be stored as VehicleID (explicit alias), or found in dispatchData.vehicleObject
          const vehicleId = user.VehicleID || user.dispatchData?.vehicleObject?.id || user.dispatchData?.vehicleObject?.Id || user.dispatchData?.vehicleId;
          const req = {
            driverId: user.Id || user.id,
            vehicleId: vehicleId,
          };
    
          console.log("comms.getInspectionItems req:", req);
    
          const response = await axios.get(`${fleetSettings.fleetUrl}/API/Driver/Inspection`, {
            params: { id: user.Id },
            headers: {
              'Content-Type': 'application/json',
            },
          });
    
          console.log("comms.getInspectionItems res:", response.data);
          if (!response.data.error) {
            return response.data;
          } else {
            throw new Error(response.data.error);
          }
    
        } catch (error) {
          console.error("comms.getInspectionItems error:", error);
          throw error;
        }
    },
    postInspectionItems: async (data) => {
      try {
          const user = JSON.parse(localStorage.getItem('userSettings')) || {};
                const fleetSettings=JSON.parse(localStorage.getItem('fleetSettings'))

          // VehicleID is the VEHICLE's Id (not the driver's Id)
          const vehicleId = user.VehicleID || user.dispatchData?.vehicleObject?.id || user.dispatchData?.vehicleObject?.Id || user.dispatchData?.vehicleId;
          const req = {
          ...data,
          driverId: user.Id || user.id,
          vehicleId: vehicleId,
          };

          console.log("comms.postInspectionItems req:", req);

          const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/Inspection`, req, {
          headers: {
              'Content-Type': 'application/json',
          },
          });

          console.log("comms.postInspectionItems res:", response.data);
          if (!response.data.error) {
          return response.data;
          } else {
          throw new Error(response.data.error);
          }

      } catch (error) {
          console.error("comms.postInspectionItems error:", error);
          throw error;
      }
    },
      /**
       * Send a message through the socket connection
       * If offline, queues the message for later sync with original timestamp
       * 
       * @param {string} message - Message to send
       * @param {Object} options - Options for offline handling
       * @param {Function} options.onOptimisticUpdate - Callback for optimistic UI update
       * @param {boolean} options.skipOfflineQueue - If true, don't queue when offline (for LiveDispatch)
       * @param {number} [options.ackTimeoutMs] - Max wait for Socket.IO emit ack (default 5000). Server often omits ack.
       * @returns {Promise} - Resolves when sent or queued
       */
    sendSocket: function(message, options = {}) {
        const userSettings=JSON.parse(localStorage.getItem('userSettings'))
   
        let driverNumber=localStorage.getItem('driverNumber')
        try{
            driverNumber=JSON.parse(localStorage.getItem('driverNumber'))
        }catch(e){}

        
        if (!driverNumber || !outGoingMessageNumber) {
            console.error('Driver number not found');
            return Promise.reject(new Error('Driver number not found'));
        }

        const messageFormat = (userSettings.unitId || driverNumber) + ":" + outGoingMessageNumber + "|" + message;
        const timestamp = Date.now(); // Capture when action occurred
        outGoingMessageNumber++;
        
        // Apply optimistic update immediately if provided
        if (options.onOptimisticUpdate) {
            options.onOptimisticUpdate();
        }

        // If socket connected, try to send immediately.
        // Many Tranware handlers do not call the Socket.IO ack callback; without a timeout the
        // Promise would never resolve (e.g. Flag Trip modal stuck on "Submitting...").
        if (socketConnection && socketConnection.connected) {
            const ackMs =
                typeof options.ackTimeoutMs === 'number' && options.ackTimeoutMs > 0
                    ? options.ackTimeoutMs
                    : 5000;
            return new Promise((resolve) => {
                let settled = false;
                const finish = (payload) => {
                    if (settled) return;
                    settled = true;
                    resolve(payload);
                };
                const timer = setTimeout(() => {
                    // The Tranware protocol does NOT use Socket.IO acks for most messages —
                    // the server's response (e.g. STARTM for START, JOBMF for FLAGM) IS
                    // the ack, and outgoing ACK|nn messages are themselves acks (never
                    // ack'd back). Only emit a noisy warning when the caller explicitly
                    // opted in via options.expectAck. Otherwise log at debug level so the
                    // console isn't drowned in benign timeout messages.
                    if (options.expectAck) {
                        console.warn(
                            'sendSocket: no server ack within',
                            ackMs,
                            'ms — treating as sent. Message:',
                            String(message).slice(0, 80)
                        );
                    } else {
                        // eslint-disable-next-line no-console
                        console.debug(
                            'sendSocket: ack timeout (expected for protocol). Message:',
                            String(message).slice(0, 80)
                        );
                    }
                    finish({ sent: true, ackTimeout: true });
                }, ackMs);
                socketConnection.emit('Message', messageFormat, (response) => {
                    clearTimeout(timer);
                    console.log('Message sent:', messageFormat);
                    finish({ sent: true, response });
                });
            });
        }
        
        // For LiveDispatch mode (skipOfflineQueue=true), don't queue - just fail silently
        // LiveDispatch requires active connection and messages like ACPTM expire quickly
        if (options.skipOfflineQueue) {
            console.log('Offline - skipping queue for LiveDispatch message:', message);
            return Promise.resolve({ sent: false, queued: false, reason: 'offline_not_queued' });
        }
        
        // #8 Offline HTTP fallback: try delivering the pipe-delimited message via
        // POST /API/Driver/Dispatch before queueing, so driver→dispatch commands
        // still reach the server's inbound queue when the socket is down. Only on
        // failure do we fall back to the durable offline queue (flushed on reconnect).
        console.log('Offline - attempting HTTP dispatch fallback:', messageFormat);
        return comms.sendDispatchMessage(messageFormat)
            .then((res) => {
                if (res && (res.status === true || res.success === true)) {
                    console.log('Offline - delivered via HTTP fallback:', messageFormat);
                    return { sent: true, viaHttp: true };
                }
                throw new Error('HTTP dispatch fallback did not confirm delivery');
            })
            .catch((err) => {
                console.log('Offline - HTTP fallback failed, queueing message:', messageFormat, err?.message);
                const actionId = offlineStorage.queueAction({
                    type: 'Message',
                    data: { message, driverNumber, unitId: userSettings?.unitId },
                    messageFormat: messageFormat,
                    timestamp: timestamp, // When the action actually occurred
                });
                return { sent: false, queued: true, actionId };
            });
    },

    assignVehicleToRoute: async function(routeId, vehicleId, scheduleId, scheduleName, sourceType='itinerary', driverId=null) {
              const fleetSettings=JSON.parse(localStorage.getItem('fleetSettings'))

        const url = `${fleetSettings.fleetUrl}/API/assignVehicleToRoute`;
        try {
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ routeId: routeId, vehicleId, scheduleId, scheduleName, sourceType, driverId }),
          });
          const data = await response.json();
          return data;
        } catch (error) {
          console.error("Error assigning vehicle to route:", error);
          throw error;
        }
      },


      /**
       * Get list of all fixed routes
       * @returns {Promise} - Promise resolving to list of routes
       */
      getFixedRouteList: async function() {
        const fleetSettings=JSON.parse(localStorage.getItem('fleetSettings'))
        const url = `${fleetSettings.fleetUrl}/api/fixedRouteList`;
        try {
          const response = await fetch(url);
          const data = await response.json();
          return data;
        } catch (error) {
          console.error("Error fetching fixed route list:", error);
          throw error;
        }
      },
      
      /**
       * Save passenger data for a fixed route stop
       * @param {Object} data - Passenger data
       * @returns {Promise} - Promise resolving to response
       */
      saveFixedRoutePassengerData: async function(data) {
        const fleetSettings=JSON.parse(localStorage.getItem('fleetSettings'))
        const url = `${fleetSettings.fleetUrl}/api/fixedRoutePassengerData`;
        try {
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
          });
          return await response.json();
        } catch (error) {
          console.error("Error saving passenger data:", error);
          throw error;
        }
      },
      
      /**
       * Get passenger data for a fixed route
       * @param {string} routeId - ID of route
       * @param {string} date - Date in YYYY-MM-DD format (optional)
       * @returns {Promise} - Promise resolving to passenger data
       */
      getFixedRoutePassengerData: async function(routeId, date) {
                const fleetSettings=JSON.parse(localStorage.getItem('fleetSettings'))
        const dateParam = date ? `&date=${date}` : '';
        const url = `${fleetSettings.fleetUrl}/api/fixedRoutePassengerData?routeId=${routeId}${dateParam}`;
        try {
          const response = await fetch(url);
          const data = await response.json();
          return data;
        } catch (error) {
          console.error("Error fetching passenger data:", error);
          throw error;
        }
      },
    
      // Driver Status Functions (LiveDispatch - require active connection, no offline queue)
      logon: (driverNumber, pin) => {
        const userSettings = JSON.parse(localStorage.getItem('userSettings'));
        if (!socketConnection || !userSettings) {
            console.error('Socket connection or user settings not available');
            return;
        }
        comms.sendSocket(`LOGON|${pin.toString().padStart(4, "0")}`, { skipOfflineQueue: true });
    },
    
    logoff: () => {
        const userSettings = JSON.parse(localStorage.getItem('userSettings'));
        if (!socketConnection || !userSettings) {
            console.error('Socket connection or user settings not available');
            return;
        }
        comms.sendSocket('LOGOF', { skipOfflineQueue: true });
    },
    
    setBusy: () => {
        comms.sendSocket('BUSY', { skipOfflineQueue: true });
    },
    
    setClear: () => {
        comms.sendSocket('CLEAR', { skipOfflineQueue: true });
    },
    
    // Trip Status Functions (LiveDispatch - require active connection, no offline queue)
    acceptTrip: (tripNumber) => {
        comms.sendSocket(`ACPTM|${tripNumber}`, { skipOfflineQueue: true });
    },
    
    rejectTrip: (tripNumber) => {
        comms.sendSocket(`REJCM|${tripNumber}`, { skipOfflineQueue: true });
    },
    
    setEnroute: (tripNumber) => {
        comms.sendSocket(`ENRTE|${tripNumber}`, { skipOfflineQueue: true });
    },
    
    setArrivedOrigin: (tripNumber) => {
        comms.sendSocket(`ORIG|${tripNumber}`, { skipOfflineQueue: true });
    },
    
    setLoaded: (tripNumber) => {
        comms.sendSocket(`LOADM|${tripNumber}`, { skipOfflineQueue: true });
    },
    
    setArrivedDestination: (tripNumber) => {
        comms.sendSocket(`DEST|${tripNumber}`, { skipOfflineQueue: true });
    },
    
    setNoShow: (tripNumber) => {
        comms.sendSocket(`NOSHM|${tripNumber}`, { skipOfflineQueue: true });
    },
    
    setCompleted: (tripNumber) => {
        comms.sendSocket(`COMPM|${tripNumber}`, { skipOfflineQueue: true });
    },
    
    /**
     * Send PRICE message with pricing items array before completion.
     *
     * Wire format: `PRICE|<tripNumber>|<JSON [items]>`. The server's PRICE handler
     * (lib/dispatch.js jobUpdatePriceItems) does `async.eachSeries(JSON.parse(payload), …)`
     * which only works on arrays — passing an object like `{items, disposition}` causes
     * eachSeries to iterate the object's values (the items array itself, then the
     * disposition object) and crash on `priceItem.name.indexOf("Wait")` because neither
     * value has a string `name`. Disposition fields (tip/toll/surcharge) are accepted as
     * a parameter for forward compatibility but are NOT sent on the wire today; if/when
     * the server grows a disposition channel, route them through a separate message.
     */
    sendPrice: (tripNumber, priceItems, _dispositionUpdates = null) => {
        const items = Array.isArray(priceItems) ? priceItems : [];
        const json = JSON.stringify(items);
        comms.sendSocket(`PRICE|${tripNumber}|${json}`, { skipOfflineQueue: true });
    },

    // PRICE-before-COMPM: send PRICE, wait for server PRICE|Ok (see setupSocketHandlers), then COMPM.
    // `paymentRequired` (PAYMENT_DRIVERAPP_HANDOFF.md §4): for a card trip, a missing
    // PRICE ack must NEVER fall through to COMPM — that would complete the trip
    // without a confirmed final price (and therefore without collecting payment).
    // Cash/account trips keep the existing timeout->COMPM fallback since there's no
    // collection step gating them.
    completeWithPricing: (tripNumber, priceItems, dispositionUpdates = null, paymentRequired = false) => {
        const trip = String(tripNumber ?? '').trim();
        if (!trip) {
            console.error('completeWithPricing: missing trip number');
            return;
        }
        if (pendingPriceComplete) {
            console.warn(
                'completeWithPricing: clearing prior pending ack for trip',
                pendingPriceComplete.tripNumber
            );
            clearPendingPriceComplete();
        }
        pendingPriceComplete = {
            tripNumber: trip,
            timeoutId: setTimeout(() => {
                if (!pendingPriceComplete || String(pendingPriceComplete.tripNumber).trim() !== trip) {
                    return;
                }
                if (paymentRequired) {
                    console.error('PRICE server ack timeout for a payment-required trip — NOT sending COMPM for trip', trip);
                    clearPendingPriceComplete();
                    return;
                }
                console.warn('PRICE server ack timeout; sending COMPM anyway for trip', trip);
                clearPendingPriceComplete();
                comms.sendSocket(`COMPM|${trip}`, { skipOfflineQueue: true });
            }, PRICE_ACK_TIMEOUT_MS),
        };
        comms.sendPrice(trip, priceItems, dispositionUpdates);
    },
    
    /**
     * Finalize a metered fare WITHOUT completing the trip: send PRICE and resolve on the
     * server's PRICE|Ok. Used before QR / hosted-card / cash collection so the server's
     * payment state (priceFinal, payable, cardAmount…) is authoritative before any
     * collection is requested. Never falls through to COMPM on timeout — the trip stays
     * active and the caller offers Retry. Not queued offline.
     *
     * @returns {Promise<{tripNumber:string}>}
     */
    finalizePrice: (tripNumber, priceItems) => {
        const trip = String(tripNumber ?? '').trim();
        if (!trip) return Promise.reject(new Error('finalizePrice: missing trip number'));
        if (!socketConnection || !socketConnection.connected) {
            return Promise.reject(new Error('Not connected to dispatch. Reconnect and try again.'));
        }
        // One outstanding PRICE per socket: a stale completion ack would otherwise send
        // COMPM the moment this finalize is acknowledged.
        if (pendingPriceComplete) {
            console.warn('finalizePrice: clearing prior pending completion for trip', pendingPriceComplete.tripNumber);
            clearPendingPriceComplete();
        }
        rejectPriceFinalize(new Error('Superseded by a newer fare finalization.'));
        return new Promise((resolve, reject) => {
            const listener = priceAckListenerFor(trip);
            socketConnection.on('Command', listener);
            pendingPriceFinalize = {
                tripNumber: trip,
                resolve,
                reject,
                socket: socketConnection,
                listener,
                timeoutId: setTimeout(() => {
                    if (pendingPriceFinalize && String(pendingPriceFinalize.tripNumber) === trip) {
                        console.error('PRICE ack timeout while finalizing fare for trip', trip, '— NOT completing');
                        rejectPriceFinalize(new Error('Dispatch did not confirm the fare. Please retry.'));
                    }
                }, PRICE_ACK_TIMEOUT_MS),
            };
            comms.sendPrice(trip, priceItems);
        });
    },

    triggerCallout: (tripNumber) => {
        comms.sendSocket(`CALLM|${tripNumber}`, { skipOfflineQueue: true });
    },
    
    updatePayment: (tripNumber, paymentType, amount) => {
        // paymentType: CREDIT, CASH, or ACCOUNT
        comms.sendSocket(`EPMTM|${tripNumber}|${paymentType}|${amount || ''}`, { skipOfflineQueue: true });
    },
    
    // Communication Functions (LiveDispatch chat - require active connection)
    sendMessage: (message) => {
        comms.sendSocket(`MSG|${message}`, { skipOfflineQueue: true });
    },

    // ── Emergency / panic alarm ──────────────────────────────────────────────
    // Sends a high-priority distress alert to dispatch. The Tranware protocol has
    // no dedicated emergency command, so for guaranteed visibility we deliver
    // through the existing MSG| dispatch channel (the operator already receives
    // and displays these). Delivery is DURABLE (no skipOfflineQueue): if the
    // socket is down it falls back to HTTP and the offline queue so a panic alert
    // is never silently dropped. A best-effort structured EMERGENCY| command is
    // also emitted in case the backend ever routes it (ignored otherwise).
    sendEmergencyAlert: async (payload = {}) => {
        const { latitude, longitude } = payload;
        const hasCoords = Number.isFinite(latitude) && Number.isFinite(longitude);
        let userSettings = {};
        try { userSettings = JSON.parse(localStorage.getItem('userSettings')) || {}; } catch (e) {}
        const driverName = userSettings.Name || userSettings.name || '';
        const who = driverName ? ` (${driverName})` : '';
        const loc = hasCoords ? `${latitude.toFixed(6)},${longitude.toFixed(6)}` : 'location unavailable';
        const mapLink = hasCoords ? ` https://maps.google.com/?q=${latitude},${longitude}` : '';
        const text = `*** EMERGENCY ALARM *** driver requesting immediate assistance${who} @ ${loc}${mapLink}`;
        // Best-effort structured command — do not block the guaranteed path on it.
        try {
            comms.sendSocket(`EMERGENCY|${JSON.stringify({ ...payload, name: driverName, ts: Date.now() })}`, {
                skipOfflineQueue: true,
                ackTimeoutMs: 4000,
            });
        } catch (e) { /* ignore — MSG path below is the guaranteed delivery */ }
        // Guaranteed, durable dispatch alert (queues/HTTP-falls-back if offline).
        return comms.sendSocket(`MSG|${text}`, { ackTimeoutMs: 4000 });
    },

    // Driver "stand down" — tells dispatch the emergency is resolved.
    cancelEmergencyAlert: async (payload = {}) => {
        let userSettings = {};
        try { userSettings = JSON.parse(localStorage.getItem('userSettings')) || {}; } catch (e) {}
        const driverName = userSettings.Name || userSettings.name || '';
        const who = driverName ? ` (${driverName})` : '';
        return comms.sendSocket(`MSG|EMERGENCY CANCELLED — driver stood down${who}`, { ackTimeoutMs: 4000 });
    },
    
    // Convert a GeolocationPosition speed (meters/second) to whole MPH.
    // Returns 0 when speed is unavailable/invalid (matches server default).
    mpsToMph: (speedMs) =>
        Number.isFinite(speedMs) && speedMs >= 0 ? Math.round(speedMs * 2.23694) : 0,

    // GPS via legacy message format (LiveDispatch - skip offline queue)
    sendGPS: (latitude, longitude, speed = 0) => {
        const latDir = latitude >= 0 ? 'N' : 'S';
        const lonDir = longitude >= 0 ? 'E' : 'W';
        const gpsString = `${latDir} ${Math.abs(latitude)},${lonDir} ${Math.abs(longitude)},${speed}`;
        comms.sendSocket(`GPS|${gpsString}`, { skipOfflineQueue: true });
    },

    /** Register handler for MSG while Flag Trip waits for reverse-geocode / create errors (cleared on modal close). */
    setFlagTripAwaitingMessageHandler: (fn) => {
        flagTripAwaitingMessageHandler = typeof fn === 'function' ? fn : null;
    },

    /** Register handler for parsed TRIPERR payloads while a flag-trip modal is open (cleared on close). */
    setFlagTripErrorHandler: (fn) => {
        flagTripErrorHandler = typeof fn === 'function' ? fn : null;
    },

    // LiveDispatch: record unscheduled flag trip (server routes FLAGM -> dispatch.createFlagTrip).
    // `payload` matches the extended FLAGM contract (docs/DriverRequirement/03_create_and_flag_trip.md
    // "Live creation"): { location:{lat,lng}, origin?:{lat,lng}, destination?, rateId?, passengerCount?,
    // mileage?, quoteToken?, prepayRef? }. `location`/`origin` accept {lat,lng} and are converted to the
    // wire's {latitude,longitude}. Server-side quoteToken/prepayRef enforcement is currently inert
    // (flagTripPolicy.enabled is off in every deployed fleet config as of July 2026) but is sent
    // whenever available so this keeps working once a fleet turns policy enforcement on.
    sendFlagTrip: function (payload) {
        const loc = (v) => (v && v.lat != null && v.lng != null ? { latitude: v.lat, longitude: v.lng } : undefined);
        const flagData = {};
        if (payload.location) flagData.location = loc(payload.location);
        if (payload.origin) flagData.origin = loc(payload.origin);
        // Client-resolved pickup address (reverse-geocoded from the GPS fix), merged INTO
        // origin/location rather than sent as a separate key: the server reads these exact
        // field names off the origin object (flagTripPolicy.normalizeLocation reads
        // streetNumber/streetName/city/state/postalCode/address), and ignores anything
        // top-level. Without this the origin arrives as bare coordinates, the server falls
        // back to its own reverse geocode, and a missing house number is persisted as the
        // literal '0' — the "0 <street>" pickups seen on flag trips.
        //
        // Coordinates are untouched, and the quoteToken fingerprint covers only origin
        // lat/lng (flagTripPolicy.canonicalFingerprint), so this cannot invalidate a quote.
        if (payload.originAddress) {
            const addr = payload.originAddress;
            if (flagData.origin) flagData.origin = { ...flagData.origin, ...addr };
            if (flagData.location) flagData.location = { ...flagData.location, ...addr };
        }
        if (payload.destination) flagData.destination = payload.destination;
        if (payload.rateId) flagData.rateId = payload.rateId;
        if (payload.quoteToken) flagData.quoteToken = payload.quoteToken;
        if (payload.prepayRef) flagData.prepayRef = payload.prepayRef;
        if (typeof payload.mileage === 'number') flagData.mileage = payload.mileage;
        const n = parseInt(payload.passengerCount, 10);
        if (!Number.isNaN(n) && n >= 1) {
            flagData.passengerCount = n;
        }
        return comms.sendSocket(`FLAGM|${JSON.stringify(flagData)}`, {
            skipOfflineQueue: true,
            ackTimeoutMs: 4000,
        });
    },

    // Pre-trip HBSS collection for a flag trip whose rate's paymentTiming is "before_submit"
    // (docs/DriverRequirement/03_flag_trip_client_handoff.md). `quoteInput` must be the EXACT
    // fields the matching quoteFlagTrip call used — the server re-derives the quote's fingerprint
    // from it to verify quoteToken/quoteInput agreement. Reply arrives via the 'flagTripPrepayUrl'
    // (link created) and 'flagTripPrepayResult' (settlement, async/webhook-driven) listeners
    // registered in setupSocketHandlers — this call itself does not return the result.
    requestFlagTripPrepayment: ({ vehicleId, driverId, routeId, quoteToken, quoteInput, presentation }) => {
        if (!socketConnection || !socketConnection.connected) {
            console.error('Socket not connected - cannot request flag trip prepayment');
            return false;
        }
        socketConnection.emit('driverAppMessage', {
            command: 'requestFlagTripPrepayment',
            data: { vehicleId, driverId, routeId, quoteToken, quoteInput, presentation },
        });
        return true;
    },

    // Manual "check payment status" refresh for a known prepayRef — covers a missed
    // flagTripPrepayResult webhook (socket blip, backgrounded PWA). The server both
    // calls back AND re-emits 'flagTripPrepayResult' on the socket, so either path
    // updates useFlagTripQuote's listener.
    getFlagTripPrepayment: ({ prepayRef }, callback) => {
        if (!socketConnection || !socketConnection.connected) {
            callback && callback({ success: false, error: 'Socket not connected' });
            return;
        }
        socketConnection.emit(
            'driverAppMessage',
            { command: 'getFlagTripPrepayment', data: { prepayRef } },
            callback
        );
    },

    // Reconnect/resume recovery for a pending or already-settled pre-trip collection matching
    // this exact quote (same vehicleId + quoteToken + quoteInput fingerprint). Callback-only —
    // the server does not also broadcast this one, per driverAppMessage:getRecoverableFlagTripPrepayment.
    getRecoverableFlagTripPrepayment: ({ vehicleId, quoteToken, quoteInput }, callback) => {
        if (!socketConnection || !socketConnection.connected) {
            callback && callback({ success: false, error: 'Socket not connected' });
            return;
        }
        socketConnection.emit(
            'driverAppMessage',
            { command: 'getRecoverableFlagTripPrepayment', data: { vehicleId, quoteToken, quoteInput } },
            callback
        );
    },

    /**
     * Send GPS update via socket (fire-and-forget)
     * GPS updates are NOT queued for offline sync since server doesn't ACK them.
     * If offline, GPS updates are simply not sent - they're not critical.
     */
    sendGPSUpdate: (latitude, longitude, speed, vehicleId, routeId, routeType, routeGroupId, vehicleObject = null) => {
        // Only send if socket is connected - GPS updates are fire-and-forget
        if (!socketConnection || !socketConnection.connected) {
            // Silently skip - GPS updates are not critical for offline sync
            return;
        }
        
        const timestamp = Date.now();
        // Read fleetSettings fresh — the module-level copy is captured once at import
        // and is null on a fresh session, which would send unitId: undefined.
        const currentFleetSettings = JSON.parse(localStorage.getItem('fleetSettings') || '{}');
        const gpsUpdateData = {
            latitude: latitude,
            longitude: longitude,
            speed: speed,
            vehicleId: vehicleId,
            vehicleObject: vehicleObject,
            routeId: routeId,
            routeType: routeType,
            routeGroupId: routeGroupId,
            timestamp: new Date(timestamp).toISOString(),
            originalTimestamp: timestamp,
            unitId: currentFleetSettings?.unitId
        };
        
        // Fire-and-forget - no callback, no queueing
        socketConnection.emit('GPS_UPDATE', gpsUpdateData);
    },

    joinRouteGroup: (unitId, vehicleId, routeId, routeType, routeGroupId) => {
        const groupData = {
            unitId: unitId,
            vehicleId: vehicleId,
            routeId: routeId,
            routeType: routeType,
            routeGroupId: routeGroupId
        };
        
        if (socketConnection) {
            socketConnection.emit('joinRouteGroup', groupData);
        }
    },

    leaveRouteGroup: (vehicleId, routeId, routeGroupId) => {
        if (socketConnection) {
            socketConnection.emit('leaveRouteGroup', {
                vehicleId: vehicleId,
                routeId: routeId,
                routeGroupId: routeGroupId
            });
        }
    },
    
    // Socket Message Handlers
    setupSocketHandlers: (socket, callbacks = {}) => {
        if (!socket) return;
        
        // Handle incoming commands from server
        socket.on('Command', (command) => {
             const parts = command.split('|').slice(1); // Skip the first part which is the message ID
            const commandType = parts[0];
            
            switch (commandType) {
                case 'START':
                    // Vehicle status at logon/refresh, straight from AutoDispatch
                    // (lib/Sender.js sendStart). Plain START carries the status only;
                    // assigned work arrives as STARTM with a jobs array instead.
                    //
                    // AD emits exactly three codes here (Sender.js startCode):
                    //   ' ' -> Clear   (may be followed by zone data: 'START| |Zn3, 2',
                    //                   which split('|') leaves as a bare ' ' in parts[1])
                    //   'O' -> OffService
                    //   'N' -> Busy
                    //
                    // Every branch MUST notify, including the unknown default. This
                    // handler is the only thing that corrects the status the app
                    // restored from localStorage, so a code that falls through silently
                    // leaves the PREVIOUS session's status on screen — showing a driver
                    // with no assigned trip as Busy/OffService and hiding the status
                    // buttons that would let them fix it.
                    {
                        const startCode = parts[1];
                        if (startCode === 'O') {
                            callbacks.onStart && callbacks.onStart('OffService');
                        }
                        else if (startCode === 'N') {
                            callbacks.onStart && callbacks.onStart('Busy');
                        }
                        else {
                            // ' ' (Clear, with or without zone) and anything unrecognised.
                            // No jobs accompany a plain START, so this clears any stale
                            // trip/vehicle status rather than leaving it untouched.
                            if (startCode !== ' ' && String(startCode ?? '').trim() !== '') {
                                console.warn('Unknown START status code, treating as Clear:', startCode);
                            }
                            callbacks.onStartNoJobs && callbacks.onStartNoJobs();
                        }
                    }
                    break;
                case 'STARTM':
                case 'STARTN':
                    // STARTN: same jobs-array payload as STARTM on some dispatch builds
                    callbacks.onStartM && callbacks.onStartM(parts[1]);
                    break;
                case 'LOGON':
                    callbacks.onLogon && callbacks.onLogon(parts[1]);
                    break;
                case 'LOGOF':
                    callbacks.onLogoff && callbacks.onLogoff(parts[1]);
                    break;
                case 'BUSY':
                    callbacks.onBusy && callbacks.onBusy(parts[1]);
                    break;
                case 'CLEAR':
                    callbacks.onClear && callbacks.onClear(parts[1]);
                    break;
                case 'PING':
                    callbacks.onPing && callbacks.onPing();
                    break;
                case 'MSG': {
                    const msgText = parts[1];
                    if (flagTripAwaitingMessageHandler) {
                        try {
                            flagTripAwaitingMessageHandler(msgText);
                        } catch (err) {
                            console.error('flagTripAwaitingMessageHandler error:', err);
                        }
                    }
                    callbacks.onMessage && callbacks.onMessage(msgText);
                    break;
                }
                case 'MSGN':
                    callbacks.onMessageBulk && callbacks.onMessageBulk(parts[1]);
                    break;
                case 'MSGA':
                    callbacks.onMessageReceived && callbacks.onMessageReceived(parts[1]);
                    break;
                case 'JOBOM':
                    // Rejoin parts[2..n]: the offer JSON carries addresses, passenger
                    // names and formattedTextTA HTML, any of which can contain a pipe.
                    // Passing only parts[2] truncated the JSON, so JSON.parse threw and
                    // the offer modal was never shown - while the auto-accept timer kept
                    // running, so the trip was taken with no Accept/Reject ever offered.
                    // JOBM below already rejoins for exactly this reason.
                    callbacks.onJobOffer && callbacks.onJobOffer(parts[1], parts.slice(2).join('|'));
                    break;
                case 'JOBM':
                    // parts[1] = trip number, parts[2..n] = job JSON (rejoin in case JSON contains pipes)
                    callbacks.onJobDetails && callbacks.onJobDetails(parts[1], parts.slice(2).join('|'));
                    break;
                case 'JOBMF':
                    // Same rejoin as JOBM — job JSON can contain pipe characters.
                    callbacks.onFlagTrip && callbacks.onFlagTrip(parts[1], parts.slice(2).join('|'));
                    break;
                case 'TRIPERR': {
                    // Live-mode flag-trip / lifecycle errors (docs/DriverRequirement/
                    // 03_flag_trip_client_handoff.md): {action, tripNumber, tripId, code, message,
                    // paymentState?}. Rejoin in case the JSON contains pipes, same as JOBM/JOBMF.
                    try {
                        const errObj = JSON.parse(parts.slice(1).join('|'));
                        callbacks.onTripError && callbacks.onTripError(errObj);
                        flagTripErrorHandler && flagTripErrorHandler(errObj);
                    } catch (e) {
                        console.error('Failed to parse TRIPERR payload:', e, parts.slice(1).join('|'));
                    }
                    break;
                }
                case 'DELJM':
                    callbacks.onDeleteJob && callbacks.onDeleteJob(parts[1]);
                    break;
                case 'PMNTM':
                    callbacks.onSetInAppPayment && callbacks.onSetInAppPayment(parts[1]);
                    break;
                case 'LOADM':
                    callbacks.onSetLoaded && callbacks.onSetLoaded(parts[1]);
                    break;
                case 'COMPM':
                    callbacks.onSetCompleted && callbacks.onSetCompleted(parts[1]);
                    break;
                case 'PRICE': {
                    const t1 = parts[1] != null ? String(parts[1]).trim() : '';
                    const t2 = parts[2] != null ? String(parts[2]).trim() : '';
                    const isOk = (s) => String(s).toLowerCase() === 'ok';
                    let ok = false;
                    let tripFromAck = '';
                    if (isOk(t1) && t2) {
                        ok = true;
                        tripFromAck = t2;
                    } else if (t1 && isOk(t2)) {
                        ok = true;
                        tripFromAck = t1;
                    } else if (isOk(t1) && !t2) {
                        ok = true;
                        tripFromAck = '';
                    }
                    if (ok) {
                        const tripHint =
                            tripFromAck ||
                            (pendingPriceComplete ? String(pendingPriceComplete.tripNumber).trim() : '') ||
                            (pendingPriceFinalize ? String(pendingPriceFinalize.tripNumber).trim() : '');
                        callbacks.onPriceOk && callbacks.onPriceOk(tripHint, true);
                        // Payment-first finalize (no COMPM) and the legacy PRICE→COMPM
                        // completion are mutually exclusive; only the pending one acts.
                        tryResolvePriceFinalize(tripFromAck);
                        tryCompleteAfterPriceAck(tripFromAck);
                    } else {
                        callbacks.onPriceOk && callbacks.onPriceOk(t1, false);
                        const detail = t2 || t1;
                        rejectPriceFinalize(new Error(detail ? `Dispatch rejected the fare: ${detail}` : 'Dispatch rejected the fare.'));
                    }
                    break;
                }
                case 'EPMTM':
                    callbacks.onRiderAppPayDriver && callbacks.onRiderAppPayDriver(parts[1], parts[2]);
                    break;
                case 'DRIVER':
                    callbacks.onSetDriver && callbacks.onSetDriver(JSON.parse(parts[1]));
                    break;
                case 'BOOK':
                    callbacks.onBookInZone && callbacks.onBookInZone(parts[1]);
                    break;
                case 'SOONM':
                    callbacks.onSoon && callbacks.onSoon(parts[1]);
                    break;
                case 'ZONEA':
                    callbacks.onZoneUpdate && callbacks.onZoneUpdate(parts[1]);
                    break;
                default:
                    console.log('Unknown command type:', commandType);
            }
        });

        // At-completion collection (Mode C) — hosted-page link/QR for the finalized
        // fare, and the "Paid" push once it settles (docs/DriverRequirement/
        // PAYMENT_DRIVERAPP_HANDOFF.md §2-§3). Plain named events, not pipe-parsed
        // Command strings — mirrors the driverAppMessage/chatHistory pattern below.
        socket.on('tripCollectionUrl', (data) => {
            callbacks.onTripCollectionUrl && callbacks.onTripCollectionUrl(data);
        });

        // Terminal collection outcome — distinct from tripCollectionUrl (link created)
        // and trip:paymentSettled (the authoritative Paid push, which can also arrive
        // from a RiderApp-initiated QR payment this DriverApp never requested).
        socket.on('tripCollectionResult', (data) => {
            callbacks.onTripCollectionResult && callbacks.onTripCollectionResult(data);
        });

        socket.on('trip:paymentSettled', (data) => {
            callbacks.onTripPaymentSettled && callbacks.onTripPaymentSettled(data);
        });

        // Reply to getTripReceipt / sendTripReceiptSms: { success, tripId, receiptUrl,
        // messageSid?, code?, error? }. A text is only "sent" once messageSid is present.
        socket.on('tripReceiptResult', (data) => {
            callbacks.onTripReceiptResult && callbacks.onTripReceiptResult(data);
        });

        // Pre-trip flag-trip collection (paymentTiming:"before_submit") — link created, then
        // settlement, which arrives asynchronously (webhook-driven), same two-event shape as
        // the at-completion collection above but keyed by prepayRef instead of tripId (there is
        // no TripID yet at this point in the flag-trip flow).
        socket.on('flagTripPrepayUrl', (data) => {
            callbacks.onFlagTripPrepayUrl && callbacks.onFlagTripPrepayUrl(data);
        });
        socket.on('flagTripPrepayResult', (data) => {
            callbacks.onFlagTripPrepayResult && callbacks.onFlagTripPrepayResult(data);
        });
    },

    // Utility Functions
    getCurrentTripStatus: () => {
        return JSON.parse(localStorage.getItem('currentTripStatus')) || null;
    },
    
    setCurrentTripStatus: (status) => {
        localStorage.setItem('currentTripStatus', JSON.stringify(status));
    },
    
    getCurrentVehicleStatus: () => {
        // Null (not 'OffService') when nothing is cached: a fresh sign-in has no status
        // yet, and defaulting to OffService made a brand-new driver look off-shift until
        // AutoDispatch's START arrived. Callers choose their own starting value; the
        // server remains the authority either way.
        return localStorage.getItem('currentVehicleStatus') || null;
    },
    
    setCurrentVehicleStatus: (status) => {
        localStorage.setItem('currentVehicleStatus', status);
    },

    /**
     * Drop the cached trip/vehicle status for the session that just ended.
     *
     * These two keys are the app's only memory of "what was I doing", and they are
     * per-DRIVER, not per-device: leaving them behind means the next driver to sign in
     * on the same tablet inherits the previous driver's status. Until AutoDispatch's
     * START arrives, that stale value is what the footer shows — a driver with no
     * assigned trip reading "Busy" or "OffService", and (because the Busy/Clear
     * buttons only render for those exact states) sometimes no way to correct it.
     *
     * Call this wherever a driver session ends: logout, shift completion, and fleet
     * change. It is deliberately NOT called on a plain reload, where the same driver
     * is still on shift and the cached status is the correct thing to show.
     */
    clearCurrentStatus: () => {
        localStorage.removeItem('currentTripStatus');
        localStorage.removeItem('currentVehicleStatus');
    },
    
    // Driver odometer functions (for future use)
    sendStartMileage: (mileage) => {
        comms.sendSocket(`DMILE|${mileage}`);
    },
    
    sendEndMileage: (mileage) => {
        comms.sendSocket(`SMILE|${mileage}`);
    },
    
    sendAcceptMileage: (tripNumber, mileage) => {
        comms.sendSocket(`AMILM|${tripNumber}|${mileage}`);
    },
    
    sendBeginMileage: (tripNumber, mileage) => {
        comms.sendSocket(`BMILM|${tripNumber}|${mileage}`);
    },
    
    // SoftMeter functions — send meter telemetry and final distance to dispatch
    sendMeter: (stats) => {
        const msg =
          `totalDistance: ${Math.round(stats.totalDistance * 100) / 100}` +
          ` totalTime: ${stats.totalTime}` +
          ` distanceFare: ${Math.round(stats.distanceFare * 100) / 100}` +
          ` timeFare: ${stats.timeFare}` +
          ` gpsSpeed: ${stats.gpsSpeed}` +
          ` fareMode: ${stats.fareMode}`;
        comms.sendSocket(`METER|${stats.jobNumber}|${msg}`, { skipOfflineQueue: true });
    },

    sendMeterDistance: (jobNumber, distanceValue) => {
        // Mirrors Java MessageGenerator.distance(): scale 1, round up, remove decimal point
        const rounded = Math.ceil(distanceValue * 10) / 10;  // round up to 1 decimal
        const encoded = rounded.toFixed(1).replace('.', '');
        comms.sendSocket(`MDIST|${jobNumber}|${encoded}`, { skipOfflineQueue: true });
    },
    
    /**
     * Driver-assisted payment dialogue — the three OTP commands that identify the
     * paying passenger before a card page is requested (DRIVERAPP_OTP_SPEC.md).
     *
     * All three share one wire shape and one reply discipline:
     *
     *   socket.emit('driverAppMessage', { command, data }, result => …)
     *
     * AutoDispatch answers BOTH ways — through the acknowledgment callback and on a
     * '<command>Result' event carrying the identical object. These helpers resolve on
     * whichever arrives first and then ignore the other, because the spec is explicit
     * that consuming both as two UI transitions would double-submit the dialogue.
     *
     * They resolve (never reject) with the server's own
     * { success:false, tripId, code, error } on a failure, so callers branch on
     * documented codes (OTP_INVALID, OTP_EXPIRED, SMS_DELIVERY_FAILED…) rather than on
     * exception text. A dropped socket resolves the same way with a synthetic code.
     */
    sendTripPaymentDialogueCommand: (command, data, { timeoutMs = 20000 } = {}) => {
        const resultEvent = command + 'Result';
        return new Promise((resolve) => {
            if (!socketConnection || !socketConnection.connected) {
                resolve({ success: false, tripId: data?.tripId, code: 'SOCKET_DISCONNECTED', error: 'Not connected to dispatch. Reconnect and try again.' });
                return;
            }
            let settled = false;
            // One resolution wins; the loser is discarded together with its listener and
            // timer, so a late duplicate can never re-enter the dialogue's state machine.
            const finish = (result) => {
                if (settled) return;
                settled = true;
                clearTimeout(timer);
                socketConnection.off && socketConnection.off(resultEvent, onEvent);
                socketConnection.removeListener && socketConnection.removeListener(resultEvent, onEvent);
                resolve(result && typeof result === 'object'
                    ? result
                    : { success: false, tripId: data?.tripId, code: 'PAYMENT_STATE_UNAVAILABLE', error: 'Dispatch sent no reply.' });
            };
            // Scoped by tripId: another trip's reply on the shared socket is not ours.
            const onEvent = (result) => {
                const replyTrip = String(result?.tripId ?? '').trim().toLowerCase();
                const askedTrip = String(data?.tripId ?? '').trim().toLowerCase();
                if (replyTrip && askedTrip && replyTrip !== askedTrip) return;
                finish(result);
            };
            const timer = setTimeout(
                () => finish({ success: false, tripId: data?.tripId, code: 'PAYMENT_STATE_UNAVAILABLE', error: 'Dispatch did not respond. Try again.' }),
                timeoutMs
            );
            socketConnection.on(resultEvent, onEvent);
            // The nested envelope is the documented contract; AD reads `data.data || data`.
            socketConnection.emit('driverAppMessage', { command, data }, finish);
        });
    },

    /**
     * Find the passenger contacts already registered against a phone number.
     *
     * Sends no SMS and changes nothing on the trip — it is safe to call while the
     * driver is still typing a number. Resolves
     * { success, tripId, contacts, canCreate, otp, payment }: zero matches with
     * canCreate:true, or one/many matches that the driver must choose between.
     * A contact is never auto-created here, and a lone match is still selected
     * explicitly rather than assumed.
     */
    lookupTripPaymentContacts: ({ tripId, phone } = {}) =>
        comms.sendTripPaymentDialogueCommand('lookupTripPaymentContacts', { tripId, phone }),

    /**
     * Create or select the contact and text them a verification code.
     *
     * Pass `contactId` for an existing match, or `name` (optional — AD names a blank
     * contact "New User") to create one. This creates the contact but does NOT yet
     * assign it to the trip; assignment happens only on confirmation, so an abandoned
     * dialogue leaves an unused contact behind, which the spec accepts.
     *
     * Resolves { success, tripId, contact, challengeId, digits, verification,
     * expiresInSeconds, code? }. `code` is present ONLY in four-digit client mode and
     * is a comparison value, not something to show the driver: it must never be
     * rendered, logged, or persisted anywhere.
     */
    requestTripPaymentOtp: ({ tripId, phone, contactId, name } = {}) => {
        const data = { tripId, phone };
        if (contactId) data.contactId = contactId;
        // Only send `name` on the create path; on the select path it is meaningless.
        else if (name !== undefined && name !== null) data.name = String(name).trim();
        return comms.sendTripPaymentDialogueCommand('requestTripPaymentOtp', data);
    },

    /**
     * Consume the challenge and assign the passenger to the trip.
     *
     * Client mode sends { confirmed:true } after the app has compared the entered
     * digits locally. Server mode sends the entered { code } and lets AD decide —
     * `confirmed:true` is rejected there and must never be substituted, which is why
     * the two arguments are mutually exclusive below.
     *
     * On success AD replaces Trip.Passenger_ContactId (any existing passenger may be
     * replaced) while Reservation.Account_ContactId — the billing account — is left
     * untouched. Success is NOT payment: the caller still has to request collection.
     */
    confirmTripPaymentContact: ({ tripId, challengeId, code, confirmed } = {}) => {
        const data = { tripId, challengeId };
        if (confirmed === true) data.confirmed = true;
        else data.code = String(code ?? '');
        return comms.sendTripPaymentDialogueCommand('confirmTripPaymentContact', data);
    },

    /**
     * Authoritative payment state straight from the dialogue's own socket command.
     *
     * Distinct from getTripPaymentState() below, which reads the RiderApp's HTTP
     * summary endpoint: this one also returns the fleet's `otp` capability and is the
     * reconciliation call after a reconnect or an uncertain confirmation.
     * Resolves { success, tripId, payment, otp }.
     */
    /**
     * Look up the trip's durable hosted collection (DRIVERAPP_HOSTED_PAGE_PAYMENTS.md
     * §"Pending payment and reconnect decisions").
     *
     * Call this only AFTER getTripPaymentState reports a pending payment: the state
     * lookup is what expires a stale durable collection, and it is the payment
     * authority. Resolves { success, tripId, collectionRef, status, amount, url } for a
     * live collection, or { success, tripId, collection: null } when there is none.
     *
     * Reopen the returned url; never mint a new hosted page just because the popup was
     * closed or the socket reconnected. A Created collection can exist before its url
     * does, so a null url means "wait and query again", not "not pending". A missing
     * collection is NOT proof the trip is unpaid — only payment state proves that.
     *
     * Callback-only: AD sends no matching result event for this command.
     */
    getTripCollectionSocket: ({ tripId } = {}, { timeoutMs = 20000 } = {}) =>
        new Promise((resolve) => {
            if (!socketConnection || !socketConnection.connected) {
                resolve({ success: false, tripId, code: 'SOCKET_DISCONNECTED', error: 'Not connected to dispatch. Reconnect and try again.' });
                return;
            }
            let settled = false;
            const finish = (result) => {
                if (settled) return;
                settled = true;
                clearTimeout(timer);
                resolve(result && typeof result === 'object'
                    ? result
                    : { success: false, tripId, code: 'PAYMENT_STATE_UNAVAILABLE', error: 'Dispatch sent no reply.' });
            };
            const timer = setTimeout(
                () => finish({ success: false, tripId, code: 'PAYMENT_STATE_UNAVAILABLE', error: 'Dispatch did not reply in time.' }),
                timeoutMs
            );
            socketConnection.emit('driverAppMessage', {
                command: 'getTripCollection',
                data: { tripId },
                tripId,
            }, finish);
        }),

    /**
     * Cancel this DriverApp's wait on a hosted collection.
     *
     * This cancels the LOCAL waiter, not the provider link: the rider may still complete
     * a page that is already open. Honour `stillPending` in the reply — while it is
     * true the link can still settle, so cash or a second collection must stay blocked.
     * Resolves { success, tripId, cancelled, stillPending, ... }.
     */
    cancelTripPaymentSocket: ({ tripId } = {}) =>
        comms.sendTripPaymentDialogueCommand('cancelTripPayment', { tripId }),

    getTripPaymentStateSocket: ({ tripId } = {}) =>
        comms.sendTripPaymentDialogueCommand('getTripPaymentState', { tripId }),
    /**
     * Ask AutoDispatch for an HBSS hosted card page for the finalized trip
     * (DRIVERAPP_HOSTED_PAGE_PAYMENTS.md §"Manual hosted-page socket contract").
     *
     * `fareAmount` is the server-supplied cardAmount (base fare + tolls + configured CC
     * surcharge), `tipAmount` the selected tip (>= 0) and `totalAmount` their sum — the
     * DriverApp never calculates a fee of its own. The nested `data` envelope is
     * required. Replies arrive on 'tripCollectionUrl' (link created — NOT paid) and
     * 'tripCollectionResult' / 'trip:paymentSettled' (settlement).
     *
     * The RiderApp QR path must NOT call this: that QR is a RiderApp lookup URL, not an
     * HBSS link.
     *
     * @returns {boolean} false when the socket is down (nothing is queued — an outstanding
     *   hosted collection is never an offline action).
     */
    /**
     * Request the hosted card page for a trip, as ONE bundle: the fare, any changed
     * price items, and the total selected tip (TRIP_PAYMENT_WORKFLOWS "Payment amount
     * audit and tip correction").
     *
     * `items` are absolute CALCULATED amounts against existing Trip_Price ids — never
     * unit rates, never increments. Omitted rows keep their stored amount, so only
     * changed rows are sent. Sending them here is what removes the need for a separate
     * PRICE call for collection-time additions: AD applies the items, the tip and the
     * payment in a single SQL transaction, and only on confirmed settlement.
     */
    requestTripCollection: ({ vehicleId, tripId, fareAmount, tipAmount = 0, totalAmount, items, presentation = 'embedded' } = {}) => {
        if (!socketConnection || !socketConnection.connected) {
            console.error('Socket not connected - cannot request trip collection');
            return false;
        }
        const fare = Math.round(Number(fareAmount) * 100) / 100;
        const tip = Math.max(0, Math.round(Number(tipAmount || 0) * 100) / 100);
        const total = totalAmount === undefined || totalAmount === null
            ? Math.round((fare + tip) * 100) / 100
            : Math.round(Number(totalAmount) * 100) / 100;
        const data = { tripId, fareAmount: fare, tipAmount: tip, totalAmount: total, presentation };
        if (vehicleId) data.vehicleId = vehicleId;
        // Only send `items` when there is at least one change. An empty array is
        // meaningless to AD and an INVALID_ITEMS risk for no benefit.
        if (Array.isArray(items) && items.length) {
            data.items = items.map((it) => {
                const row = { id: String(it.id), amount: Math.round(Number(it.amount) * 100) / 100 };
                // qty is the stored unit count, sent only when the driver actually set one.
                if (it.qty !== undefined && it.qty !== null && it.qty !== '') {
                    row.qty = Number(it.qty);
                }
                return row;
            });
        }
        // Dual-shape payload for server-version tolerance:
        //   - nested `data`  → current handler (`data.data || data`), the documented contract;
        //   - top level      → older deployed builds, which read `amount`/`tripId` directly
        //                      and would otherwise see no amount at all.
        // `amount` is the legacy fare-only field and must carry the TOTAL, since an old
        // server charges exactly what it finds there and knows nothing about tips.
        socketConnection.emit('driverAppMessage', {
            command: 'requestTripCollection',
            data,
            ...data,
            amount: total,
        });
        return true;
    },

    // View receipt: AutoDispatch issues the signed hosted receipt URL — the DriverApp
    // never constructs one. Reply on 'tripReceiptResult'.
    getTripReceipt: ({ tripId, paymentMethod } = {}) => {
        if (!socketConnection || !socketConnection.connected) {
            console.error('Socket not connected - cannot request trip receipt');
            return false;
        }
        const data = { tripId };
        if (paymentMethod) data.paymentMethod = paymentMethod;
        socketConnection.emit('driverAppMessage', { command: 'getTripReceipt', data });
        return true;
    },

    // Text receipt: AutoDispatch normalizes the number and sends the hosted receipt link
    // through its own Twilio credentials. No API key or receipt body leaves the app.
    // paymentMethod: 'Cash' | 'Credit Card' | 'Wallet'. Reply on 'tripReceiptResult'.
    sendTripReceiptSms: ({ tripId, mobileNumber, paymentMethod } = {}) => {
        if (!socketConnection || !socketConnection.connected) {
            console.error('Socket not connected - cannot text trip receipt');
            return false;
        }
        socketConnection.emit('driverAppMessage', {
            command: 'sendTripReceiptSms',
            data: { tripId, mobileNumber, paymentMethod },
        });
        return true;
    },

    /**
     * Reload the authoritative payment summary for one trip after PRICE|Ok (or when the
     * payment sheet opens): GET /API/Payment/active-trips?tripId= — the same
     * paymentAPI.summarizeTrip() row the RiderApp shows, including baseFare / tolls /
     * cardSurcharge / cardAmount and the has* flags. Resolves null when the trip is no
     * longer active or the request fails; callers keep their last known state.
     */
    getTripPaymentState: async ({ tripId } = {}) => {
        const id = String(tripId ?? '').trim();
        if (!id) return null;
        try {
            const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
            if (!fleetSettings.fleetUrl) return null;
            const response = await axios.get(`${fleetSettings.fleetUrl}/API/Payment/active-trips`, {
                params: { tripId: id },
                timeout: 8000,
            });
            const trips = Array.isArray(response.data?.trips) ? response.data.trips : [];
            const match = trips.find((t) => String(t?.tripId || '').trim().toLowerCase() === id.toLowerCase());
            return match || null;
        } catch (error) {
            console.warn('getTripPaymentState failed for trip', id, error?.message || error);
            return null;
        }
    },

    getChatHistory: (vehicleNumber, callback) => {
    if (socketConnection && socketConnection.connected) {
      socketConnection.emit('driverAppMessage', {
        command: 'getChatHistory',  
        vehicleNumber: vehicleNumber
      });
      
      // Listen for the response
      socketConnection.once('chatHistory', callback);
    } else {
      console.error('Socket not connected - cannot get chat history');
      callback({ error: 'Socket not connected' });
    }
  },

  // Get driver schedule (itinerary + trip fallback) for a specific date
  getDriverSchedule: async (driverId, date) => {
    try {
      // Read fleetSettings fresh from localStorage (the module-level copy is captured
      // once at import time and is null/stale on a fresh session — see line ~48).
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) {
        throw new Error('Fleet settings not available (no fleetUrl)');
      }
      // API doc expects date as YYYY-MM-DD. Normalize any YYYY/MM/DD callers.
      const normalizedDate = date ? String(date).replace(/\//g, '-') : date;
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/Schedule`, {
        driverId: driverId,
        date: normalizedDate // YYYY-MM-DD format
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching driver schedule:', error);
      throw error;
    }
  },

  // ── Driver dashboard feature endpoints ──────────────────────────────
  // All read fleetSettings fresh from localStorage and target the fleet's
  // AutoDispatch host (e.g. https://twezr-nj.qryde.net:8079) via fleetUrl.
  // Endpoints/bodies follow AutoDispatch_API_Documentation.md.

  // POST /API/Driver/History  { driverId }  → past trips
  getDriverHistory: async (driverId) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/History`, { driverId });
      return response.data;
    } catch (error) {
      console.error('Error fetching driver history:', error);
      throw error;
    }
  },

  // POST /API/Driver/Balance  { driverId }  (proxy to SaaS RemoteCashiering)
  getDriverBalance: async (driverId) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/Balance`, { driverId });
      return response.data;
    } catch (error) {
      console.error('Error fetching driver balance:', error);
      throw error;
    }
  },

  // POST /API/Driver/Transactions  { driverId }
  getDriverTransactions: async (driverId) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/Transactions`, { driverId });
      return response.data;
    } catch (error) {
      console.error('Error fetching driver transactions:', error);
      throw error;
    }
  },

  // POST /API/Driver/Deposits  { driverId }
  getDriverDeposits: async (driverId) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/Deposits`, { driverId });
      return response.data;
    } catch (error) {
      console.error('Error fetching driver deposits:', error);
      throw error;
    }
  },

  // POST /API/Driver/Payments  { driverId }
  getDriverPayments: async (driverId) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/Payments`, { driverId });
      return response.data;
    } catch (error) {
      console.error('Error fetching driver payments:', error);
      throw error;
    }
  },

  // POST /API/Driver/Cashiering  { driverId } -> { available, pending, trips: [{tripNo, when, amount, status}] }
  // Backs the Cashiering screen (docs/DriverRequirement/05_cashiering_and_financials, 08_driverapp_cashiering_handoff.md).
  getDriverCashiering: async (driverId) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/Cashiering`, { driverId });
      return response.data;
    } catch (error) {
      console.error('Error fetching driver cashiering:', error);
      throw error;
    }
  },

  // POST /API/Driver/Ledger  { driverId } -> { balance, entries: [{date, description, direction, amount, runningBalance, status}] }
  // Backs the single-list Transactions/Ledger screen.
  getDriverLedger: async (driverId) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/Ledger`, { driverId });
      return response.data;
    } catch (error) {
      console.error('Error fetching driver ledger:', error);
      throw error;
    }
  },

  // POST /API/Driver/ScheduleDrop  { driverId, amount } -> { ok, scheduledAmount, newPending } or { ok:false, error, available? }
  // Schedules an ACH drop against the driver's available balance.
  scheduleAchDrop: async (driverId, amount) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/ScheduleDrop`, { driverId, amount });
      return response.data;
    } catch (error) {
      console.error('Error scheduling ACH drop:', error);
      throw error;
    }
  },

  // The server (lib/weeklyPlanning/availability.js) stores availability as flat
  // one-window-per-row CRUD over Driver_Schedule (GET/POST/PUT/DELETE
  // /API/Driver/Availability, keyed by id+driverId) — it has no concept of a
  // "day" with segments/breaks/startLocation, and no batch "save the week"
  // endpoint. Rather than a DB migration, we store ONE row per day, spanning
  // that day's earliest segment start to latest segment end (or the full day
  // when unavailable/no segments), and encode the richer
  // { available, segments, startLocationId, breaks } shape as JSON in the
  // row's existing Description field (nvarchar(2000), already free-text).
  // This is a pragmatic bridge, not the "real" fix — if this graduates beyond
  // ACET, the segments/breaks should get their own columns/tables server-side.

  // GET /API/Driver/Availability?driverId=<guid> -> { status, windows: [...] }
  // Returns the driver's saved weekly availability, decoded back into the
  // per-day shape the UI uses. Callers use optional chaining since this may
  // 404 / return disabled on fleets that haven't enabled weeklyPlanning.
  getDriverAvailability: async (driverId) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.get(`${fleetSettings.fleetUrl}/API/Driver/Availability`, {
        params: { driverId },
      });
      const windows = response.data?.windows || [];
      const days = windows.map((w) => {
        let decoded = {};
        try {
          decoded = w.Description ? JSON.parse(w.Description) : {};
        } catch {
          decoded = {};
        }
        const date = String(w.FromDateTime || '').slice(0, 10);
        return {
          windowId: w.Id,
          date,
          available: decoded.available ?? true,
          segments: Array.isArray(decoded.segments) ? decoded.segments : [],
          startLocationId: decoded.startLocationId || null,
          startLocationName: decoded.startLocationName || '',
          breaks: Array.isArray(decoded.breaks) ? decoded.breaks : [],
        };
      });
      return { status: response.data?.status !== false, days };
    } catch (error) {
      console.error('Error fetching driver availability:', error);
      throw error;
    }
  },

  // Persists each day as one Driver_Schedule row: PUT if we already have a
  // windowId for that date (from a prior load), POST to create otherwise.
  // payload: { driverId, days: [{ windowId?, date, available, segments, startLocationId, breaks }] }
  // Returns { status, days: [{ date, windowId }] } (updated ids, for days that were created).
  saveDriverAvailability: async (payload) => {
    const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
    if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
    const { driverId, days = [] } = payload;
    const results = [];
    for (const day of days) {
      const segments = Array.isArray(day.segments) ? day.segments : [];
      const times = segments.flatMap((s) => [s.startTime, s.endTime]).filter(Boolean).sort();
      const fromTime = day.available && times.length ? times[0] : '00:00';
      const toTime = day.available && times.length ? times[times.length - 1] : '23:59';
      const description = JSON.stringify({
        available: !!day.available,
        segments,
        startLocationId: day.startLocationId || null,
        startLocationName: day.startLocationName || '',
        breaks: Array.isArray(day.breaks) ? day.breaks : [],
      });
      const body = {
        driverId,
        fromDateTime: `${day.date}T${fromTime}`,
        toDateTime: `${day.date}T${toTime}`,
        description,
      };
      try {
        if (day.windowId) {
          const res = await axios.put(`${fleetSettings.fleetUrl}/API/Driver/Availability`, {
            id: day.windowId,
            driverId,
            ...body,
          });
          results.push({ date: day.date, windowId: day.windowId, ok: res.data?.status !== false });
        } else {
          const res = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/Availability`, body);
          results.push({ date: day.date, windowId: res.data?.id, ok: res.data?.status !== false });
        }
      } catch (error) {
        console.error(`Error saving availability for ${day.date}:`, error);
        results.push({ date: day.date, windowId: day.windowId, ok: false });
      }
    }
    return { status: results.every((r) => r.ok), days: results };
  },

  // POST /API/Driver/Receipt  { driverId, receiptId }  (receiptId required)
  getDriverReceipt: async (driverId, receiptId) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/Receipt`, { driverId, receiptId });
      return response.data;
    } catch (error) {
      console.error('Error fetching driver receipt:', error);
      throw error;
    }
  },

  // POST /API/Driver/CashieringSummary  { driverId, date }
  // Today's Balance Owed + pending Cash/Account/Voucher breakdown + Summary
  // figures. Not deployed on every fleet yet — see docs/CASHIERING_BACKEND_API_SPEC.md.
  getCashieringSummary: async (driverId, date) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/CashieringSummary`, { driverId, date });
      return response.data;
    } catch (error) {
      console.error('Error fetching cashiering summary:', error);
      throw error;
    }
  },

  // POST /API/Driver/Receipts  { driverId, fromDate, toDate }
  // List of daily receipts for the Cashiering HISTORY tab (distinct from the
  // single-lookup getDriverReceipt above). See docs/CASHIERING_BACKEND_API_SPEC.md.
  getDriverReceipts: async (driverId, { fromDate, toDate } = {}) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/Receipts`, { driverId, fromDate, toDate });
      return response.data;
    } catch (error) {
      console.error('Error fetching driver receipts:', error);
      throw error;
    }
  },

  // POST /API/Driver/Deductions  { driverId, receiptId }
  // Itemized deduction lines for Receipt Detail. See docs/CASHIERING_BACKEND_API_SPEC.md.
  getDriverDeductions: async (driverId, receiptId) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/Deductions`, { driverId, receiptId });
      return response.data;
    } catch (error) {
      console.error('Error fetching driver deductions:', error);
      throw error;
    }
  },

  // POST /API/Driver/Adjustments  { driverId, receiptId }
  // Itemized adjustment lines for Receipt Detail. See docs/CASHIERING_BACKEND_API_SPEC.md.
  getDriverAdjustments: async (driverId, receiptId) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/Adjustments`, { driverId, receiptId });
      return response.data;
    } catch (error) {
      console.error('Error fetching driver adjustments:', error);
      throw error;
    }
  },

  // POST /API/Trips/Receipt  { tripId, phone?, email? }
  // Sends a trip receipt to the rider by SMS and/or email.
  sendReceipt: async (tripId, { phone, email } = {}) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const body = { tripId };
      if (phone) body.phone = phone;
      if (email) body.email = email;
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Trips/Receipt`, body);
      return response.data;
    } catch (error) {
      console.error('Error sending receipt:', error);
      throw error;
    }
  },

  // POST /API/Driver/Cashier  { driverId, lineItems }
  // Records driver cash activity (e.g. a cash deposit) against the live financial
  // ledger. In mock mode we record the deposit locally so the Cash screen's
  // deposit flow can be tested end-to-end. The LIVE write is still gated: the
  // real Cashier endpoint requires an undocumented lineItem schema (a
  // driverBalanceId GUID + glAccountId + a required decimal) and rejects partial
  // payloads with "Cannot convert null to 'decimal'", so we do not blindly post
  // guessed values to a shared production ledger.
  driverCashier: async (driverId, lineItems) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/Cashier`, { driverId, lineItems });
      return response.data;
    } catch (error) {
      console.error('Error posting driver cashier line items:', error);
      throw error;
    }
  },

  // POST /API/Trips/Update  { apiKey, tripId, ...fields }
  // Persists driver edits to a booking. Like trip creation, the fleet requires
  // the fleet apiKey on this write; without it the server rejects the request.
  // In mock mode we echo the change back so the edit UI can be tested end-to-end
  // without touching a live booking.
  updateTrip: async (tripId, changes) => {
    const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
    if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
    const apiKey =
      fleetSettings.apiKey || fleetSettings.ApiKey || fleetSettings.fleetApiKey || fleetSettings.FleetApiKey;
    if (!apiKey) {
      throw new Error('Editing bookings requires a fleet apiKey, which is not configured for this fleet.');
    }
    try {
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Trips/Update`, {
        apiKey,
        tripId,
        ...changes,
      });
      return response.data;
    } catch (error) {
      console.error('Error updating trip:', error);
      throw error;
    }
  },

  // GET /API/Driver/Messages?Id=<driverId>
  getDriverMessages: async (driverId) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.get(`${fleetSettings.fleetUrl}/API/Driver/Messages`, {
        params: { Id: driverId }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching driver messages:', error);
      throw error;
    }
  },

  // Add getSocket method for accessing the socket connection
  getSocket: () => {
    return socketConnection;
  },

  // Join driver app room 
  joinDriverApp: () => {
    const userSettings = JSON.parse(localStorage.getItem('userSettings'));
    // Shared resolver — same as joinDriverAppRoom (see utils/resolveVehicleUnitId.js).
    const unitID = resolveVehicleUnitId({ userSettings });
    if (socketConnection && userSettings && unitID) {
      console.log('Joining driver app room for unit:', unitID);
      socketConnection.emit('joinDriverApp', { UnitID: unitID });
      return true;
    }
    return false;
  },

  // Get available vehicles for vehicle selection
  getAvailableVehicles: async () => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      const response = await axios.get(`${fleetSettings.fleetUrl}/API/Driver/Vehicles`);
        // alphabatize the vehicle list by Name
        response.data.sort((a, b) => a.Name.localeCompare(b.Name));
        // Names that are just numbers at the bottom of the list
        response.data.sort((a, b) => {
          const aIsNumeric = /^\d+$/.test(a.Name);
          const bIsNumeric = /^\d+$/.test(b.Name);
            if (aIsNumeric && !bIsNumeric) return 1;
            if (!aIsNumeric && bIsNumeric) return -1;
            return 0;
        });

      return response.data;
    } catch (error) {
      console.error('Error fetching available vehicles:', error);
      throw error;
    }
  },

  // Get available routes for route selection
  getAvailableRoutes: async (vehicleTypeId) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      const response = await axios.get(`${fleetSettings.fleetUrl}/API/Driver/Routes`, {
        params: { vehicleTypeId }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching available routes:', error);
      throw error;
    }
  },

  // Call shift close
  closeShift: async (driverId, vehicleId) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/ShiftClose`, {
        driverId,
        vehicleId
      });
      return response.data;
    } catch (error) {
      console.error('Error closing shift:', error);
      throw error;
    }
  },

  // ── Feature spec endpoints (driver.js authoritative) ────────────────

  // #1 Resume a shift the driver closed earlier today (route still scheduled).
  // Reuses the existing assignment path (POST /API/assignVehicleToRoute).
  resumeShift: async (routeId, vehicleId, driverId) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/assignVehicleToRoute`, {
        routeId,
        vehicleId,
        driverId,
        sourceType: 'itinerary',
      });
      return response.data;
    } catch (error) {
      console.error('Error resuming shift:', error);
      throw error;
    }
  },

  // #2 Driver sticker data (proxies SaaS RemoteCashiering function:'sticker').
  // POST /API/Driver/Sticker { driverId, testDate? }
  getDriverSticker: async (driverId, testDate) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const body = testDate ? { driverId, testDate } : { driverId };
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/Sticker`, body);
      return response.data;
    } catch (error) {
      console.error('Error fetching driver sticker:', error);
      throw error;
    }
  },

  // #2 Server-side printable sticker HTML from the sticker/insurance object.
  // POST /API/Driver/Sticker/Build (returns HTML string). Optional — UI can
  // fall back to client-side rendering + window.print() if this 404s.
  buildSticker: async (stickerData) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/Sticker/Build`, stickerData);
      return response.data;
    } catch (error) {
      console.error('Error building sticker HTML:', error);
      throw error;
    }
  },

  // #3 List claimable (unclaimed future) routes — route bidding.
  // GET /API/Driver/ClaimableRoutes  params: serviceDate | fromDate&toDate, vehicleTypeId
  getClaimableRoutes: async (params = {}) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.get(`${fleetSettings.fleetUrl}/API/Driver/ClaimableRoutes`, { params });
      return response.data;
    } catch (error) {
      // Route bidding is gated server-side (appVar.routeBidding.enabled, default
      // off) and may not be deployed on this fleet at all — that returns 404.
      // Treat it as "feature unavailable" and degrade gracefully instead of
      // surfacing a raw "Request failed with status code 404" to the driver.
      if (error?.response?.status === 404) {
        return { status: false, available: false, routes: [] };
      }
      console.error('Error fetching claimable routes:', error);
      throw error;
    }
  },

  // #4 Claim a route first-come-first-served (optional one-time start/end adjust).
  // POST /API/Driver/ClaimRoute { routeId, driverId, vehicleId?, startDateTime?, endDateTime? }
  claimRoute: async ({ routeId, driverId, vehicleId, startDateTime, endDateTime }) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const body = { routeId, driverId };
      if (vehicleId) body.vehicleId = vehicleId;
      if (startDateTime) body.startDateTime = startDateTime;
      if (endDateTime) body.endDateTime = endDateTime;
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/ClaimRoute`, body);
      return response.data;
    } catch (error) {
      // Endpoint not deployed on this fleet (see getClaimableRoutes note).
      if (error?.response?.status === 404) {
        return { status: false, available: false, message: 'Route bidding is not available on this fleet.' };
      }
      console.error('Error claiming route:', error);
      throw error;
    }
  },

  // Auto-assign optimized trips into a route the driver just claimed.
  // POST /API/Driver/AssignRouteTrips { routeId, driverId, vehicleId?, serviceDate?, optimizationType?, mode?, dryRun? }
  // The optimization (best-fit trips by least time/distance, within the shift &
  // capacity constraints) runs SERVER-SIDE — the app only requests it and renders
  // the result. The response mirrors getDriverSchedule's shape. LIVE action: like
  // claimRoute, never queued offline. See docs/ASSIGN_ROUTE_TRIPS_API_CONTRACT.md.
  assignRouteTrips: async ({ routeId, driverId, vehicleId, serviceDate, optimizationType, mode, dryRun } = {}) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const body = { routeId, driverId };
      if (vehicleId) body.vehicleId = vehicleId;
      if (serviceDate) body.serviceDate = serviceDate;
      if (optimizationType) body.optimizationType = optimizationType;
      if (mode) body.mode = mode;
      if (dryRun != null) body.dryRun = dryRun;
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/AssignRouteTrips`, body);
      return response.data;
    } catch (error) {
      // Endpoint not deployed on this fleet yet — degrade gracefully so the claim
      // still succeeds and dispatch can assign trips manually (no raw 404 shown).
      if (error?.response?.status === 404) {
        return { status: false, available: false, message: 'Auto-assign is not available on this fleet.' };
      }
      console.error('Error assigning route trips:', error);
      throw error;
    }
  },

  // #5 Release a route — clears Vehicle/Driver from the route.
  // POST /API/unassignVehicleFromRoute { routeId?, vehicleId? } (at least one)
  unassignVehicleFromRoute: async (routeId, vehicleId) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const body = {};
      if (routeId) body.routeId = routeId;
      if (vehicleId) body.vehicleId = vehicleId;
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/unassignVehicleFromRoute`, body);
      return response.data;
    } catch (error) {
      console.error('Error unassigning vehicle from route:', error);
      throw error;
    }
  },

  // ── Mixed-dispatch trip marketplace ──────────────────────────────────────
  // Browse unassigned trips and claim or bid on them. Gated server-side by
  // driverRouting.enabled plus driverRouting.driverSelfAssembly (claim) /
  // driverRouting.tripBidding (bid); when a flag is off the list endpoints
  // return an empty trips[] rather than an error. 404 = not deployed on this
  // fleet. LIVE actions: like claimRoute, never queued offline.

  // GET /API/Driver/AvailableTrips  params: serviceDate, driverId, vehicleId?, limit?
  getAvailableTrips: async (params = {}) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.get(`${fleetSettings.fleetUrl}/API/Driver/AvailableTrips`, { params });
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        return { status: false, available: false, trips: [] };
      }
      console.error('Error fetching available trips:', error);
      throw error;
    }
  },

  // POST /API/Driver/ClaimTrip { tripId, driverId, vehicleId?, routeId? }
  //
  // Preferred claim path: unlike the `pullTrip` socket action (which runs VROOM and so
  // only ever works for a ROUTED trip), this lets the server classify the trip itself and
  // commit a route-less live-dispatch claim. Resolves `{ available: false }` on 404 so a
  // server without this endpoint can fall back to the socket action.
  claimTrip: async ({ tripId, driverId, vehicleId, routeId } = {}) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const body = { tripId, driverId };
      if (vehicleId) body.vehicleId = vehicleId;
      if (routeId) body.routeId = routeId;
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/ClaimTrip`, body);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        return { status: false, available: false };
      }
      // A server-side rejection carries a usable reason; surface it rather than throwing.
      if (error?.response?.data) return error.response.data;
      console.error('Error claiming trip:', error);
      throw error;
    }
  },

  // POST /API/Driver/SubmitBid { tripId, driverId, vehicleId?, routeId? }
  // routeId names the target route for a routed bid and is sent when known (the trip's
  // own route, else the driver's active route). Older server builds reject a bid without
  // one, so it is forwarded rather than omitted.
  submitTripBid: async ({ tripId, driverId, vehicleId, routeId } = {}) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const body = { tripId, driverId };
      if (vehicleId) body.vehicleId = vehicleId;
      if (routeId) body.routeId = routeId;
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/SubmitBid`, body);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        return { status: false, available: false, error: 'Trip bidding is not available on this fleet.' };
      }
      console.error('Error submitting trip bid:', error);
      throw error;
    }
  },

  // POST /API/Driver/RespondOffer { offerId, action, driverId?, vehicleId? }
  // Used here with action:'withdraw' to pull back a pending bid.
  respondTripOffer: async ({ offerId, action, driverId, vehicleId } = {}) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const body = { offerId, action };
      if (driverId) body.driverId = driverId;
      if (vehicleId) body.vehicleId = vehicleId;
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/RespondOffer`, body);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        return { status: false, available: false, error: 'Offers are not available on this fleet.' };
      }
      console.error('Error responding to trip offer:', error);
      throw error;
    }
  },

  // GET /API/Driver/Offers?driverId=&vehicleId= — recover this driver's pending
  // offer/bid ids after a reload, so a bid can still be withdrawn.
  getDriverOffers: async (params = {}) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.get(`${fleetSettings.fleetUrl}/API/Driver/Offers`, { params });
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        return { status: false, available: false, offers: [] };
      }
      console.error('Error fetching driver offers:', error);
      throw error;
    }
  },

  // ── Route self-scheduling (weeklyPlanning.driverScheduling) ──────────────
  // All endpoints below are gated server-side. When the flag is off, reads
  // return empty collections and mutations return { status:false, error }.
  // 404 = feature not deployed on this fleet at all — degrade gracefully.
  // LIVE actions: like claimRoute, never queued offline.

  // Shared location catalog for route start/end points.
  // GET /API/Driver/VehicleLocations?siteId=  (siteId optional; server falls back)
  getVehicleLocations: async (siteId) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const params = siteId ? { siteId } : {};
      const response = await axios.get(`${fleetSettings.fleetUrl}/API/Driver/VehicleLocations`, { params });
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        return { status: false, available: false, locations: [] };
      }
      console.error('Error fetching vehicle locations:', error);
      throw error;
    }
  },

  // Create a new SHARED location (visible fleet-wide in the catalog).
  // POST /API/Driver/VehicleLocations { driverId, name, description?, latitude, longitude, zoneId? }
  createVehicleLocation: async ({ driverId, name, description, latitude, longitude, zoneId }) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const body = { driverId, name, latitude, longitude };
      if (description) body.description = description;
      if (zoneId) body.zoneId = zoneId;
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/VehicleLocations`, body);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        return { status: false, available: false, message: 'Location catalog is not available on this fleet.' };
      }
      console.error('Error creating vehicle location:', error);
      throw error;
    }
  },

  // The driver's own RouteMaster template(s) — weekday times, break, locations.
  // GET /API/Driver/RouteMasterTemplate?driverId=
  getRouteMasterTemplate: async (driverId) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.get(`${fleetSettings.fleetUrl}/API/Driver/RouteMasterTemplate`, {
        params: { driverId },
      });
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        return { status: false, available: false, templates: [] };
      }
      console.error('Error fetching route master template:', error);
      throw error;
    }
  },

  // Update the driver's OWN template — only supplied fields change. Scoped
  // server-side to (routeMasterId AND driverId). Times accept "HH:MM" or
  // "HH:MM:SS"; null/"" clears a field; a weekday with start/end null = off.
  // PUT /API/Driver/RouteMasterTemplate { driverId, routeMasterId, startVehicleLocationId?,
  //   endVehicleLocationId?, isInactive?, days? }
  updateRouteMasterTemplate: async (body) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.put(`${fleetSettings.fleetUrl}/API/Driver/RouteMasterTemplate`, body);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        return { status: false, available: false, message: 'Scheduling is not available on this fleet.' };
      }
      console.error('Error updating route master template:', error);
      throw error;
    }
  },

  // Per-day overrides on a route the driver owns (claimed) or a baseline from
  // their template: times, start/end locations, isInactive, and optimize:true
  // to seed the route's trips officially (server uses scheduled times).
  // POST /API/Driver/ModifyRoute { driverId, routeId, startDateTime?, endDateTime?,
  //   breakStartDateTime?, breakEndDateTime?, startVehicleLocationId?,
  //   endVehicleLocationId?, isInactive?, optimize? }
  modifyRoute: async (body) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/ModifyRoute`, body);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        return { status: false, available: false, message: 'Scheduling is not available on this fleet.' };
      }
      console.error('Error modifying route:', error);
      throw error;
    }
  },

  // "No more trips" switch. acceptingTrips:false writes Route.IsInactive —
  // dispatch stops adding trips and the route is excluded from batch
  // optimization, but it stays fully drivable with its existing trips.
  // POST /API/Driver/SetRouteAcceptingTrips { driverId, routeId, acceptingTrips }
  setRouteAcceptingTrips: async ({ driverId, routeId, acceptingTrips }) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const body = { driverId, routeId, acceptingTrips };
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/SetRouteAcceptingTrips`, body);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        return { status: false, available: false, message: 'Scheduling is not available on this fleet.' };
      }
      console.error('Error setting route accepting trips:', error);
      throw error;
    }
  },

  // Full route header + ordered steps (start, pickup, dropoff, break, end).
  // Only start/end steps' locationId/time are editable via ModifyRoute.
  // GET /API/Driver/RouteDetail?driverId=&routeId=
  getRouteDetail: async ({ driverId, routeId }) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.get(`${fleetSettings.fleetUrl}/API/Driver/RouteDetail`, {
        params: { driverId, routeId },
      });
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        return { status: false, available: false, message: 'RouteDetail is not available on this fleet.' };
      }
      console.error('Error fetching route detail:', error);
      throw error;
    }
  },

  // All routes for a driver on one service-date, each with its ordered steps.
  // Server calls ensureBaseRouteSteps before returning, so start/break/end steps
  // are present even on freshly generated routes. Primary loader for the day view.
  // GET /API/Driver/DaySchedule?driverId=&serviceDate=YYYY-MM-DD
  getDaySchedule: async ({ driverId, serviceDate }) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.get(`${fleetSettings.fleetUrl}/API/Driver/DaySchedule`, {
        params: { driverId, serviceDate },
      });
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        return { status: false, available: false, routes: [] };
      }
      console.error('Error fetching day schedule:', error);
      throw error;
    }
  },

  // Explicit activate/deactivate for one route. active:false writes Route.IsInactive=1
  // (same column as SetRouteAcceptingTrips with inverse booleans — interchangeable).
  // POST /API/Driver/SetRouteActive { driverId, routeId, active }
  setRouteActive: async ({ driverId, routeId, active }) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/SetRouteActive`, {
        driverId, routeId, active,
      });
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        return { status: false, available: false, message: 'SetRouteActive is not available on this fleet.' };
      }
      console.error('Error setting route active:', error);
      throw error;
    }
  },

  // Atomically split one trip-free future route into two or three ordered daily
  // segments. The source route is reused (and overwritten) as segment 1; later
  // segments run on dynamically-selected reserve RouteMasters the server picks —
  // the client never sends a target RouteMaster. sourceLockVersion must be the
  // CURRENT value (fetch fresh via getRouteDetail right before calling this) or
  // the server rejects with a retryable SPLIT_CONFLICT.
  // POST /API/Driver/SplitRoute { driverId, routeId, sourceLockVersion, segments }
  // segments: [{ startDateTime, endDateTime, startVehicleLocationId, endVehicleLocationId,
  //   active?, break?: { startDateTime, endDateTime, durationMinutes } | null }, ...] (2 or 3 items)
  // All datetimes must be real UTC ISO strings (trailing "Z") — this endpoint's
  // parser treats a bare "YYYY-MM-DDTHH:MM:SS" as literal UTC, unlike ModifyRoute.
  splitRoute: async ({ driverId, routeId, sourceLockVersion, segments }) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/SplitRoute`, {
        driverId, routeId, sourceLockVersion, segments,
      }, { withCredentials: true }); // send the session cookie authorize() stored
      return response.data;
    } catch (error) {
      // Validation/business-rule failures come back as a normal 200 { status:false, code, error }
      // body (handled by the caller above) — this catch only sees network errors or a 404
      // when the endpoint isn't deployed on this fleet at all.
      if (error?.response?.status === 404) {
        return { status: false, code: 'SPLIT_NOT_AVAILABLE', error: 'Splitting routes is not available on this fleet.' };
      }
      console.error('Error splitting route:', error);
      throw error;
    }
  },

  // Map a claimed route to a shift (multi-shift day: one shift per claimed route).
  // POST /API/Driver/MapRouteShift { routeId, shiftId, driverId }
  mapRouteShift: async ({ routeId, shiftId, driverId }) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const body = { routeId, shiftId, driverId };
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/MapRouteShift`, body);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        return { status: false, available: false, message: 'Multi-shift mapping is not available on this fleet.' };
      }
      console.error('Error mapping route to shift:', error);
      throw error;
    }
  },

  // "Which route now?" — drives begin-next-shift logic at the BeginShift/
  // CompleteShift boundaries on a multi-route day.
  // GET /API/Driver/ShiftAction  params: driverId, vehicleId?
  getShiftAction: async (params = {}) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.get(`${fleetSettings.fleetUrl}/API/Driver/ShiftAction`, { params });
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        return { status: false, available: false };
      }
      console.error('Error fetching shift action:', error);
      throw error;
    }
  },

  // Record a driver's acceptance of a disclaimer/consent (audit trail) — used by
  // AvailabilityConsentGate and the login safety notice. Best-effort: the caller
  // already persists acceptance locally, so a 404/failure here is safe to ignore.
  // POST /API/Driver/Consent { driverId, type, version, timestamp }
  recordConsent: async ({ driverId, type, version, timestamp }) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/Consent`, {
        driverId, type, version, timestamp: timestamp || new Date().toISOString(),
      });
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        return { status: false, available: false };
      }
      console.error('Error recording consent:', error);
      return { status: false };
    }
  },

  // #6 Update shift odometer (beginning and/or ending). At least one numeric ≥ 0.
  // POST /API/Driver/Odometer { shiftId, beginningOdometer?, endingOdometer? }
  updateShiftOdometer: async (shiftId, { beginningOdometer, endingOdometer } = {}) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const body = { shiftId };
      if (beginningOdometer != null) body.beginningOdometer = Number(beginningOdometer);
      if (endingOdometer != null) body.endingOdometer = Number(endingOdometer);
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/Odometer`, body);
      return response.data;
    } catch (error) {
      console.error('Error updating shift odometer:', error);
      throw error;
    }
  },

  // #8 HTTP fallback for driver→dispatch pipe-delimited messages when socket is down.
  // POST /API/Driver/Dispatch { msg: "UNIT01:msgId|COMMAND|payload" }
  sendDispatchMessage: async (msg) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/Dispatch`, { msg });
      return response.data;
    } catch (error) {
      console.error('Error sending dispatch message:', error);
      throw error;
    }
  },

  // Driver SMS — send an SMS to the driver via the fleet gateway.
  // POST /API/Driver/SMS { apiKey, driverNumber, message, updates? }
  // driverNumber defaults to the logged-in driver; apiKey is pulled from fleetSettings
  // when not supplied. `updates` (string "true"/"false") opts into status-update texts.
  sendDriverSMS: async (message, { driverNumber, updates } = {}) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const userSettings = JSON.parse(localStorage.getItem('userSettings')) || {};
      const body = {
        apiKey: fleetSettings.apiKey || fleetSettings.apiToken,
        driverNumber:
          driverNumber || userSettings.Code || userSettings.code || userSettings.driverNumber,
        message,
      };
      if (updates != null) body.updates = String(updates);
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/SMS`, body);
      return response.data;
    } catch (error) {
      console.error('Error sending driver SMS:', error);
      throw error;
    }
  },

  // VIN OCR — upload a photo of the VIN plate and get the decoded VIN string.
  // POST /API/Driver/getVin  (multipart/form-data): file upload field + form field
  // `image` = base64 JPG. Accepts a File/Blob, a base64 data-URL/string, or both.
  getVin: async (fileOrBase64, base64Image) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const form = new FormData();
      let base64 = base64Image;
      if (fileOrBase64 instanceof Blob) {
        form.append('file', fileOrBase64, fileOrBase64.name || 'vin.jpg');
      } else if (typeof fileOrBase64 === 'string') {
        // Caller passed the base64 string directly as the first arg.
        base64 = base64 || fileOrBase64;
      }
      if (base64) form.append('image', base64);
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/getVin`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      console.error('Error scanning VIN:', error);
      throw error;
    }
  },
};

// Dev-only console hook: expose comms (and the mock module) on window so the
// API methods can be exercised from DevTools in development. Stripped from
// production builds (NODE_ENV === 'production'); no effect on shipped app.
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  window.comms = comms;
}

export default comms;